// ============================================================
// RENDERER
// ============================================================

import {
  BATTLE_COLS, BATTLE_ROWS, M3_COLS, M3_ROWS,
  ELEM, ECOL, SPELLS, UCFG, MAX_SWAPS_PER_ROUND,
  pointsToLevel, SPELL_LEVEL_THRESHOLDS, SPELL_LEVEL_MAX,
} from './constants.ts';
import { getLeader, LEADERS } from './balance.ts';

/**
 * Session 9 — short label for the HUD element bar. Default = first
 * char of the ELEM name, but `WindFriendly` / `WindHostile` clash on
 * "W" so spell them out as `W+` / `W−` (visually distinct, fits the
 * same character-width budget as the other one-letter labels).
 */
function _elemShort(elName: string): string {
  if (elName === 'WindFriendly') return 'W+';
  if (elName === 'WindHostile') return 'W−';
  // Session 11 (Leader CCC) — Grow / Rain explicit labels. Grow keeps
  // its 'G' single-char; Rain takes 'Rn' since 'R' could be mistaken
  // for the rooted-debuff badge glyph rendered separately on units.
  if (elName === 'Grow') return 'G';
  if (elName === 'Rain') return 'Rn';
  // Session 12 (Leader CCC) — Rockfall takes 'Rk' to disambiguate from
  // Rain ('Rn') so the HUD bar shows them side-by-side without confusion.
  if (elName === 'Rockfall') return 'Rk';
  return elName[0];
}

/**
 * Session 10 — derive a short SCHED-list description from the spell's
 * effects[] list (no longer a single `sp.type`). Pick the dominant
 * effect kind for the label; chain '+' if multiple kinds present.
 *
 * Display priority: damage > heal > buffAtk > shield > whirlwindPull.
 * For whirlwind, append a scope tag so the player can tell ally-pull
 * from foe-pull at a glance.
 */
function _spellDesc(sd: { effects?: ReadonlyArray<{ kind: string }>; scope?: 'own'|'enemy' } | undefined): string {
  if (!sd?.effects || sd.effects.length === 0) return '—';
  const kinds = new Set(sd.effects.map((e) => e.kind));
  const parts: string[] = [];
  if (kinds.has('damage')) parts.push('Dmg');
  if (kinds.has('heal')) parts.push('Heal');
  if (kinds.has('buffAtk')) {
    // Negative-only buffAtk (i.e. debuff) shows as 'Debuff'; otherwise 'Buff'.
    // Heuristic: if scope is 'enemy', it's a debuff; 'own' is a buff.
    parts.push(sd.scope === 'enemy' ? 'Debuff' : 'Buff');
  }
  if (kinds.has('shieldPhys') || kinds.has('shieldMag')) parts.push('Shield');
  if (kinds.has('whirlwindPull')) {
    parts.push(sd.scope === 'enemy' ? 'Pull (Foe)' : 'Pull (Ally)');
  }
  // Session 11 (Leader CCC) — Grow / Rain dual-mechanic labels. Both
  // paint terrain (cleanly summarized as "Grass" / "Puddle") and trigger
  // a secondary effect on pre-existing terrain (root / dmg). Show as
  // 'Grass+Root' / 'Puddle+Dmg' so player sees both halves at a glance.
  if (kinds.has('growGrass')) parts.push('Grass+Root');
  if (kinds.has('rainPuddle')) parts.push('Puddle+Dmg');
  // Session 12 (Leader CCC) — Rockfall: 'Boulder/Dmg' summarizes the
  // dual mechanic (empty cell → boulder, occupied cell → big damage).
  if (kinds.has('rockfall')) parts.push('Boulder/Dmg');
  return parts.join(' + ');
}

/**
 * Session 9 follow-up — UNIQUE 1-char gem-face label per ELEM.
 *
 * The naive `ELEM[t][0]` produces duplicates that confuse the player
 * when two same-letter gems sit next to each other in different colors:
 *
 *   Water / Wind / WindFriendly / WindHostile  → all "W"
 *   Light / Lightning                          → both "L"
 *
 * Each label is picked from the SPELL the element grants (so "S" =
 * Shield Wall = Wind, "B" = Bless = Light, "Z" = Zap = Lightning), and
 * the two whirlwind variants use directional arrows that visually
 * mirror their mechanic (inward pull / outward gust).
 */
function _elemGemLabel(elName: string): string {
  switch (elName) {
    case 'Fire':         return 'F';
    case 'Water':        return 'W';
    case 'Wind':         return 'S';   // Shield Wall
    case 'Light':        return 'B';   // Bless
    case 'Lightning':    return 'Z';   // Zap
    case 'WindFriendly': return '↘';   // pulls inward
    case 'WindHostile':  return '↗';   // blows outward
    // Session 11 (Leader CCC) — sprout glyph for Grow, droplet for Rain.
    // Both glyphs single-char so they fit the same gem-face budget as
    // the existing labels.
    case 'Grow':         return '葉';
    case 'Rain':         return '雨';
    // Session 12 (Leader CCC) — Rockfall: 石 (stone) glyph; reads as
    // "boulder" alongside the other thematic Chinese glyphs.
    case 'Rockfall':     return '石';
    default:             return elName ? elName[0] : '?';
  }
}
import { md, clamp } from './utils.ts';
import { ctx, W, H, CB, CM, ZBY, ZHY, ZMY, OXB, OYB, OXM, OYM } from './canvas.ts';
import { Game, GS, FX } from './Game.ts';
import { Deployment, DeployedUnit } from './Deployment.ts';
import type { BattleUnit } from './BattleUnit.ts';
import type { TerrainCell, TerrainGrid } from './server/terrain.ts';
import { drawAvatar, speakerName } from './story/AvatarRenderer.ts';
import { isCompleted as storyChapterCompleted } from './story/StoryProgress.ts';

export class Renderer {
  draw(g: Game): void {
    ctx.clearRect(0, 0, W, H);
    this._bg();
    // Session 6c follow-up: state-specific content first, but DON'T early-
    // return — overlays (loading spinner / waiting banner / transition)
    // need to render on top of every state including MENU/LOBBY/DEPLOY.
    if (g.state === GS.MENU) {
      this._menu(g);
    } else if (g.state === GS.STORY_SELECT) {
      this._storySelect(g);
    } else if (g.state === GS.CASUAL_SELECT) {
      this._casualSelect(g);
    } else if (g.state === GS.LOBBY) {
      this._lobby(g);
    } else if (g.state === GS.DEPLOY) {
      this._deploy(g);
      // Story Mode — render arrow hint overlay over the battlefield grid.
      if (g.storyController?.arrowHint) this._storyArrow(g);
      // Story Mode — highlight expected placement cell, path cells, and
      // Start Battle button as appropriate.
      if (g.storyController) this._storyDeployHints(g);
    } else {
      this._battlefield(g);
      this._hud(g);
      if (g.state === GS.MATCH3 || g.state === GS.SCHED || g.state === GS.EXEC) {
        this._battleTabs(g);
        if (g.bottomTab === 'match3') this._match3(g);
        else this._scheduling(g);
        this._phaseBanner(g);
        // Story Mode — render forced-swap highlight on top of the M3 board
        // when the player is in MATCH3 with an expectedSwap constraint.
        if (g.state === GS.MATCH3 && g.storyController?.expectedSwap && g.bottomTab === 'match3') {
          this._storySwapHighlight(g);
        }
        // Story Mode SCHED hints — target cell highlight + Confirm glow.
        if (g.state === GS.SCHED && g.storyController) {
          this._storySchedHints(g);
        }
      }
      if (g.state === GS.OVER) this._gameOver(g);
      this._fx(g);
    }
    this._transition(g);
    // Overlays — always considered, never gated by state.
    if (g.waitingForOpponent) this._waitingOverlay(g);
    if (g.initPending) this._loadingOverlay(g);
    // Surrender button — MATCH3 only. Drawn AFTER state content so it
    // sits on top of the phase banner area but UNDER the story dialogue
    // (story dialogue covers bottom half; button is top-right corner).
    if (g.state === GS.MATCH3) this._surrenderButton(g);
    // Story Mode — dialogue box renders last so it sits above everything,
    // including loading spinner edge cases.
    if (g.storyController?.dialogueActive) this._storyDialogue(g);
    // Surrender confirm modal — on top of EVERYTHING (including dialogue)
    // because it's a hard system-level prompt.
    if (g.surrenderConfirmActive) this._surrenderConfirm(g);
  }

  // ----------------------------------------------------------
  // SURRENDER button + confirm modal
  // ----------------------------------------------------------

  /** Hit-test rect for the top-right surrender button. Input.ts shares this. */
  static surrenderButtonRect(): { x: number; y: number; w: number; h: number } {
    const w = 86;
    const h = 22;
    return { x: W - w - 6, y: 2, w, h };
  }

  /** Hit-test rect for the modal's Confirm button. */
  static surrenderConfirmRect(): { x: number; y: number; w: number; h: number } {
    const w = W * 0.32;
    const h = 40;
    const cy = H / 2 + 26;
    return { x: W / 2 + 8, y: cy, w, h };
  }

  /** Hit-test rect for the modal's Cancel button. */
  static surrenderCancelRect(): { x: number; y: number; w: number; h: number } {
    const w = W * 0.32;
    const h = 40;
    const cy = H / 2 + 26;
    return { x: W / 2 - w - 8, y: cy, w, h };
  }

  /** Top-right surrender button (long-press 5s to surrender).
   *  While `g.surrenderHolding`, a red fill grows left-to-right showing
   *  hold progress. Hidden during dialogue / loading / waiting overlays. */
  private _surrenderButton(g: Game): void {
    if (g.storyController?.dialogueActive) return;
    if (g.initPending || g.waitingForOpponent) return;
    const r = Renderer.surrenderButtonRect();
    const t = Game.SURRENDER_HOLD_SECS;
    const frac = g.surrenderHolding ? Math.min(1, g.surrenderHoldTime / t) : 0;

    // Background — subtle dark red panel
    ctx.fillStyle = 'rgba(40,16,16,0.85)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    // Hold-progress fill (red, fills L→R)
    if (frac > 0) {
      ctx.fillStyle = `rgba(220,60,60,${0.55 + frac * 0.35})`;
      ctx.fillRect(r.x, r.y, r.w * frac, r.h);
    }
    // Frame
    ctx.strokeStyle = g.surrenderHolding ? '#ff8844' : '#995544';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    // Label
    ctx.fillStyle = g.surrenderHolding ? '#ffeecc' : '#ddaa88';
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const label = g.surrenderHolding
      ? `投降中… ${Math.ceil(t - g.surrenderHoldTime)}s`
      : '投降 (長按)';
    ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  }

  /** Modal asking the player to confirm surrender. Two buttons:
   *  - Cancel (gray, left)
   *  - Confirm (red, right) */
  private _surrenderConfirm(g: Game): void {
    // Dim full-screen scrim
    ctx.fillStyle = 'rgba(0,0,0,0.72)';
    ctx.fillRect(0, 0, W, H);

    // Panel
    const panelW = W * 0.84;
    const panelH = 200;
    const px = (W - panelW) / 2;
    const py = (H - panelH) / 2 - 30;
    ctx.fillStyle = '#1a1f2c';
    ctx.fillRect(px, py, panelW, panelH);
    ctx.strokeStyle = '#aa4444';
    ctx.lineWidth = 2;
    ctx.strokeRect(px + 0.5, py + 0.5, panelW - 1, panelH - 1);

    // Title
    ctx.fillStyle = '#ffaa66';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText('確定投降？', W / 2, py + 22);

    // Subtitle — differs by mode
    ctx.fillStyle = '#bbccdd';
    ctx.font = '13px sans-serif';
    // Scripted modes (story / casual) bail back to MENU / picker instead
    // of awarding the opponent a win — subtitle reflects that.
    const isScripted = g.deployment.rules.kind === 'preset';
    const sub = isScripted
      ? (g.gameMode === 'casual' ? '將退出本關卡回到關卡選單' : '將直接退出本章節回到主畫面')
      : '對方將直接獲勝';
    ctx.fillText(sub, W / 2, py + 56);
    const sub2 = isScripted
      ? '（進度不會儲存，可再重玩）'
      : '';
    if (sub2) {
      ctx.fillStyle = '#778899';
      ctx.font = '11px sans-serif';
      ctx.fillText(sub2, W / 2, py + 78);
    }

    // Buttons
    const confirm = Renderer.surrenderConfirmRect();
    const cancel = Renderer.surrenderCancelRect();
    // Cancel
    ctx.fillStyle = '#2a2e44';
    ctx.fillRect(cancel.x, cancel.y, cancel.w, cancel.h);
    ctx.strokeStyle = '#556677';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(cancel.x + 0.5, cancel.y + 0.5, cancel.w - 1, cancel.h - 1);
    ctx.fillStyle = '#bbccdd';
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('取消', cancel.x + cancel.w / 2, cancel.y + cancel.h / 2);
    // Confirm
    ctx.fillStyle = '#5a1a1a';
    ctx.fillRect(confirm.x, confirm.y, confirm.w, confirm.h);
    ctx.strokeStyle = '#cc4444';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(confirm.x + 0.5, confirm.y + 0.5, confirm.w - 1, confirm.h - 1);
    ctx.fillStyle = '#ffaa88';
    ctx.fillText('投降', confirm.x + confirm.w / 2, confirm.y + confirm.h / 2);
  }

  /** Phase 3d Step 4 — semi-transparent banner that says "waiting for opponent". */
  private _waitingOverlay(_g: Game): void {
    const bandH = 38;
    const y = OYB - 4 - bandH;
    ctx.fillStyle = 'rgba(20,30,55,0.88)';
    ctx.fillRect(0, y, W, bandH);
    ctx.strokeStyle = '#ffaa44';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, y + bandH); ctx.lineTo(W, y + bandH); ctx.stroke();
    ctx.fillStyle = '#ffcc66';
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    // Animated dots based on time
    const t = (Date.now() / 350) | 0;
    const dots = '.'.repeat((t % 4));
    ctx.fillText('Waiting for opponent' + dots, W / 2, y + bandH / 2);
  }

  private _bg(): void {
    ctx.fillStyle = '#080c18';
    ctx.fillRect(0, 0, W, H);
  }

  // Phase 3d Step 3: menu button geometry — shared with Input.ts hit-testing.
  static readonly MENU_BTN_W = 240;
  static readonly MENU_BTN_H = 44;
  static readonly MENU_BTN_GAP = 14;

  /** Y center of menu button N (0=top). */
  static menuButtonY(idx: number): number {
    return H * 0.5 + idx * (Renderer.MENU_BTN_H + Renderer.MENU_BTN_GAP);
  }

  /** Returns the rectangle of menu button N. */
  static menuButtonRect(idx: number): { x: number; y: number; w: number; h: number } {
    const w = Renderer.MENU_BTN_W;
    const h = Renderer.MENU_BTN_H;
    return { x: (W - w) / 2, y: Renderer.menuButtonY(idx) - h / 2, w, h };
  }

  private _drawMenuButton(idx: number, label: string, color = '#44ccff'): void {
    const r = Renderer.menuButtonRect(idx);
    ctx.fillStyle = 'rgba(20,30,55,0.85)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    ctx.fillStyle = color;
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, W / 2, r.y + r.h / 2);
  }

  private _menu(g: Game): void {
    // Title
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 30px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Puzzle Tactician', W / 2, H * 0.3);
    // Subtitle
    ctx.fillStyle = '#8899bb';
    ctx.font = '14px sans-serif';
    ctx.fillText('Match-3 Auto-Battler', W / 2, H * 0.3 + 36);

    if (!g.netMode) {
      // Non-net build (e.g. `npm run dev` default): no PvP buttons, so
      // we show the three local modes as explicit equal-rank buttons.
      // Test Mode replaces the legacy "Tap anywhere" Single Player flow —
      // a real button is less confusing for first-time players.
      this._drawMenuButton(0, 'Test Mode',   '#44ccff');
      this._drawMenuButton(1, 'Story Mode',  '#ffaa66');
      this._drawMenuButton(2, 'Casual Mode', '#66ddcc');
      return;
    }

    // Net mode: 6 explicit buttons (Test, Create, Join, Server, Story, Casual).
    // "Test Mode" is the local-vs-AI sandbox — same code path as the old
    // "Single Player" button, just labelled to reflect what it's for
    // (rapid iteration / dev testing rather than a "real" matchup).
    this._drawMenuButton(0, 'Test Mode',     '#888899');
    this._drawMenuButton(1, 'Create Room',   '#44ccff');
    this._drawMenuButton(2, 'Join Room',     '#44ff88');

    // Phase 5a Step 1: Server URL editor button (idx=3).
    // Custom-drawn (two-line) instead of going through _drawMenuButton
    // so we can fit a longer URL with a smaller font.
    this._drawServerButton(g);

    // Story Mode (idx=4) — placed after PvP / Server so existing PvP
    // button indices don't shift. Distinct warm colour from the PvP
    // greens / blues.
    this._drawMenuButton(4, 'Story Mode', '#ffaa66');

    // Casual Mode (idx=5) — placed last; teal so it visually
    // distinguishes from Story's warm orange.
    this._drawMenuButton(5, 'Casual Mode', '#66ddcc');

    // Net status line — only show when adapter has actually opened.
    const adapter = g.adapter as any;
    const status = adapter?.status ?? 'idle';
    if (status !== 'idle') {
      ctx.fillStyle = '#5566aa';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('connection: ' + status,
        W / 2, H * 0.5 + 6 * (Renderer.MENU_BTN_H + Renderer.MENU_BTN_GAP) - 4);
    }
  }

  /**
   * Phase 5a Step 1: render the "Server URL — tap to edit" button at
   * menu slot idx=3. Two lines: hint label + the current host. Hit-
   * testing in Input.ts uses Renderer.menuButtonRect(3), so the rect
   * stays consistent with the standard button geometry.
   */
  private _drawServerButton(g: Game): void {
    const r = Renderer.menuButtonRect(3);
    // Background + thin border (visually de-emphasised vs the action buttons)
    ctx.fillStyle = 'rgba(20,30,55,0.6)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = '#556677';
    ctx.lineWidth = 1;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);

    // Top line: hint
    ctx.fillStyle = '#8899aa';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Server URL — tap to edit', W / 2, r.y + 12);

    // Bottom line: current URL host (truncated if too long).
    const host = g.netUrl.replace(/^wss?:\/\//, '');
    ctx.fillStyle = '#bbccdd';
    ctx.font = '12px monospace';
    const display = host.length > 30 ? host.slice(0, 14) + '…' + host.slice(-14) : host;
    ctx.fillText(display, W / 2, r.y + r.h - 14);
  }

  private _lobby(g: Game): void {
    // Title
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('PvP Lobby', W / 2, H * 0.25);

    // Mode line
    ctx.fillStyle = '#8899bb';
    ctx.font = '14px sans-serif';
    const modeLabel = g.gameMode === 'pvp-host' ? 'You are HOST (team 0)'
      : g.gameMode === 'pvp-guest' ? 'You are GUEST (team 1)'
      : '';
    ctx.fillText(modeLabel, W / 2, H * 0.25 + 30);

    // Room code. Big + "share" subtext while still pairing; small text
    // only after both deployments are submitted (no longer needs sharing).
    if (g.roomCode) {
      if (!g.awaitingBattleReady) {
        ctx.fillStyle = '#44ff88';
        ctx.font = 'bold 36px monospace';
        ctx.fillText(g.roomCode, W / 2, H * 0.4);
        ctx.fillStyle = '#5566aa';
        ctx.font = '12px sans-serif';
        ctx.fillText('share this code with your opponent', W / 2, H * 0.4 + 28);
      } else {
        ctx.fillStyle = '#556677';
        ctx.font = '14px monospace';
        ctx.fillText('Room: ' + g.roomCode, W / 2, H * 0.4);
      }
    }

    // Status message — moved up to H*0.55 to leave room for Cancel below.
    ctx.fillStyle = '#cccccc';
    ctx.font = '14px sans-serif';
    ctx.fillText(g.lobbyMessage || '...', W / 2, H * 0.55);

    // Cancel button — moved down to button slot 3 (was 2) so it doesn't
    // collide with the status message text. Input.ts hit-tests the same idx.
    this._drawMenuButton(3, 'Cancel', '#ff8866');
  }

  // ----------------------------------------------------------
  // STORY MODE — chapter list + dialogue overlay + tutorial hints
  // ----------------------------------------------------------

  /**
   * Geometry for chapter buttons on the STORY_SELECT screen.
   * Shared with Input.ts hit-testing (Input imports
   * `Renderer.storyChapterRect`).
   */
  static readonly STORY_CHAPTER_H = 64;
  static readonly STORY_CHAPTER_GAP = 12;
  /** Chapter buttons fill 90% of viewport width (capped at 320 for wide screens). */
  static storyChapterW(): number {
    return Math.min(320, W * 0.9);
  }

  static storyChapterRect(idx: number): { x: number; y: number; w: number; h: number } {
    const w = Renderer.storyChapterW();
    const h = Renderer.STORY_CHAPTER_H;
    const y = H * 0.32 + idx * (h + Renderer.STORY_CHAPTER_GAP);
    return { x: (W - w) / 2, y, w, h };
  }

  /** Back button on STORY_SELECT — return to MENU. Shared with Input.
   *  Positioned just above bottom edge so it stays in-frame on portrait
   *  phones (the chapter list grows downward from H*0.32). */
  static storyBackRect(): { x: number; y: number; w: number; h: number } {
    const w = Renderer.MENU_BTN_W;
    const h = Renderer.MENU_BTN_H;
    return { x: (W - w) / 2, y: H - h - 24, w, h };
  }

  private _storySelect(g: Game): void {
    // Title
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Story Mode', W / 2, H * 0.16);
    ctx.fillStyle = '#8899bb';
    ctx.font = '13px sans-serif';
    ctx.fillText('選擇章節開始劇情', W / 2, H * 0.16 + 26);

    const chapters = g.listChapters();
    for (let i = 0; i < chapters.length; i++) {
      const c = chapters[i];
      const r = Renderer.storyChapterRect(i);
      const done = storyChapterCompleted(c.id);
      // Background
      ctx.fillStyle = done ? 'rgba(34,68,44,0.85)' : 'rgba(20,30,55,0.85)';
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.strokeStyle = done ? '#66dd88' : '#ffaa66';
      ctx.lineWidth = 2;
      ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
      // Title
      ctx.fillStyle = done ? '#aaffbb' : '#ffeecc';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      const titleY = c.subtitle ? r.y + r.h * 0.32 : r.y + r.h / 2;
      ctx.fillText(c.title, r.x + 14, titleY);
      // Subtitle
      if (c.subtitle) {
        ctx.fillStyle = '#aabbcc';
        ctx.font = '12px sans-serif';
        ctx.fillText(c.subtitle, r.x + 14, r.y + r.h * 0.66);
      }
      // Completed check mark
      if (done) {
        ctx.fillStyle = '#66dd88';
        ctx.font = 'bold 22px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('✓', r.x + r.w - 14, r.y + r.h / 2);
      }
    }

    // Back button — reuses standard menu-button geometry at idx=5 so it
    // sits well below the chapter list regardless of chapter count.
    const back = Renderer.storyBackRect();
    ctx.fillStyle = 'rgba(20,30,55,0.85)';
    ctx.fillRect(back.x, back.y, back.w, back.h);
    ctx.strokeStyle = '#ff8866';
    ctx.lineWidth = 2;
    ctx.strokeRect(back.x + 0.5, back.y + 0.5, back.w - 1, back.h - 1);
    ctx.fillStyle = '#ff8866';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('← Back to Menu', W / 2, back.y + back.h / 2);
  }

  // ----------------------------------------------------------
  // CASUAL MODE — level picker
  //
  // Layout mirrors STORY_SELECT (same geometry helpers, same back
  // button placement) so the two pickers feel visually consistent.
  // Distinct accent colour (teal) so the player can tell modes apart
  // at a glance.
  // ----------------------------------------------------------

  /**
   * Hit-test rect for casual level button N.
   * Geometry shared between Renderer (drawing) and Input (tap routing).
   * Uses identical numbers to Story Mode so 5 levels fit comfortably on
   * portrait phones (chapter list grows from H*0.32 downward; the back
   * button at the bottom is well clear).
   */
  static casualLevelRect(idx: number): { x: number; y: number; w: number; h: number } {
    const w = Renderer.storyChapterW();
    const h = Renderer.STORY_CHAPTER_H;
    const y = H * 0.22 + idx * (h + Renderer.STORY_CHAPTER_GAP);
    return { x: (W - w) / 2, y, w, h };
  }

  /** Back button rect on CASUAL_SELECT (returns to MENU). Shared with Input. */
  static casualBackRect(): { x: number; y: number; w: number; h: number } {
    // Same geometry as storyBackRect — fixed near the bottom edge so it
    // stays consistent across screen sizes regardless of level count.
    return Renderer.storyBackRect();
  }

  /**
   * Render the casual mode level picker. 5 preset levels listed
   * vertically; tap a level to launch it, or "Back to Menu" at the
   * bottom to return to MENU.
   *
   * Casual mode intentionally does NOT track per-level completion
   * (unlike Story Mode which uses StoryProgress.isCompleted). Casual is
   * meant to be a low-pressure replayable mode — the player picks
   * whichever level they feel like.
   */
  private _casualSelect(g: Game): void {
    // Title
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 26px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Casual Mode', W / 2, H * 0.08);
    ctx.fillStyle = '#8899bb';
    ctx.font = '13px sans-serif';
    ctx.fillText('選擇關卡 — 單位固定，只需規劃路徑', W / 2, H * 0.08 + 22);

    const levels = g.listCasualLevels();
    for (let i = 0; i < levels.length; i++) {
      const lvl = levels[i];
      const r = Renderer.casualLevelRect(i);
      // Background — teal accent so casual reads distinctly from story.
      ctx.fillStyle = 'rgba(22,42,52,0.85)';
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.strokeStyle = '#66ddcc';
      ctx.lineWidth = 2;
      ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
      // Title (left-aligned)
      ctx.fillStyle = '#cdfff0';
      ctx.font = 'bold 17px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      const titleY = lvl.subtitle ? r.y + r.h * 0.32 : r.y + r.h / 2;
      ctx.fillText(lvl.title, r.x + 14, titleY);
      // Subtitle
      if (lvl.subtitle) {
        ctx.fillStyle = '#88bbb0';
        ctx.font = '12px sans-serif';
        ctx.fillText(lvl.subtitle, r.x + 14, r.y + r.h * 0.66);
      }
      // Unit count badge (right side) — quick at-a-glance roster size.
      const totalPlayer = lvl.player.length;
      const totalAi = lvl.ai.length;
      ctx.fillStyle = '#66ddcc';
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.fillText(`你 ${totalPlayer} vs 敵 ${totalAi}`, r.x + r.w - 14, r.y + r.h / 2);
    }

    // Back button — reuses STORY back geometry for layout consistency.
    const back = Renderer.casualBackRect();
    ctx.fillStyle = 'rgba(20,30,55,0.85)';
    ctx.fillRect(back.x, back.y, back.w, back.h);
    ctx.strokeStyle = '#66ddcc';
    ctx.lineWidth = 2;
    ctx.strokeRect(back.x + 0.5, back.y + 0.5, back.w - 1, back.h - 1);
    ctx.fillStyle = '#66ddcc';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('← Back to Menu', W / 2, back.y + back.h / 2);
  }

  /**
   * Dialogue box overlay — top edge aligned with `ZMY` (the match-3 /
   * interaction zone top), bottom edge at H. This is the cleanest split:
   * everything above ZMY is the battle + HUD view, everything from ZMY
   * down is the dialogue panel during story moments. Avatar on the left,
   * text on the right.
   */
  private _storyDialogue(g: Game): void {
    const ctrl = g.storyController;
    if (!ctrl || !ctrl.dialogueActive) return;
    const line = ctrl.dialogueLines[ctrl.dialogueIdx];
    if (!line) return;

    const boxY = ZMY;
    const boxH = H - ZMY;
    // Subtle scrim above to dim the active interaction zone (match3 /
    // sched UI) so the dialogue panel reads as the primary content.
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, boxY - 12, W, 12);
    // Background panel
    ctx.fillStyle = 'rgba(8,12,24,0.96)';
    ctx.fillRect(0, boxY, W, boxH);
    ctx.strokeStyle = '#ffaa66';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, boxY + 0.5); ctx.lineTo(W, boxY + 0.5);
    ctx.stroke();

    // Avatar — top-left of the panel, leaves text the rest. Size is
    // capped so portrait screens don't lose the speaker name area.
    const padding = 14;
    const avatarSize = Math.min(boxH - padding * 2 - 28, 112, W * 0.28);
    const ax = padding + avatarSize / 2;
    const ay = boxY + padding + avatarSize / 2;
    drawAvatar(line.speaker, ax, ay, avatarSize);

    // Speaker name BELOW the avatar (centered under it). Keeps the text
    // column free to use the full vertical space.
    ctx.fillStyle = '#ffcc66';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    ctx.fillText(line.name || speakerName(line.speaker), ax, ay + avatarSize / 2 + 6);

    // Dialogue body — to the right of the avatar, occupies the full
    // panel height minus padding.
    const textX = padding * 2 + avatarSize;
    const textW = W - textX - padding;
    const lineH = 22;
    const lines = this._wrapText(line.text, textW, '15px sans-serif');
    ctx.fillStyle = '#ffffff';
    ctx.font = '15px sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    let ty = boxY + padding;
    for (const wrapped of lines) {
      ctx.fillText(wrapped, textX, ty);
      ty += lineH;
      if (ty > boxY + boxH - 26) break;
    }

    // Footer row — progress dots on the left, tap hint on the right.
    const pulse = 0.65 + 0.35 * Math.sin(Date.now() / 250);
    ctx.fillStyle = '#556677';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
    ctx.fillText(`${ctrl.dialogueIdx + 1} / ${ctrl.dialogueLines.length}`, textX, H - 10);
    ctx.fillStyle = `rgba(255,204,102,${pulse})`;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'right';
    const more = ctrl.dialogueIdx < ctrl.dialogueLines.length - 1
      ? '▶ 點擊繼續'
      : '▶ 點擊關閉';
    ctx.fillText(more, W - padding, H - 10);
  }

  /** Word-wrap helper for dialogue. Returns array of lines. */
  private _wrapText(text: string, maxW: number, font: string): string[] {
    ctx.save();
    ctx.font = font;
    const out: string[] = [];
    // Split first by explicit newlines, then wrap each paragraph
    const paras = text.split('\n');
    for (const para of paras) {
      // Chinese-friendly char-by-char wrap (no spaces to split on for CJK)
      let line = '';
      for (const ch of para) {
        const test = line + ch;
        if (ctx.measureText(test).width > maxW && line) {
          out.push(line);
          line = ch;
        } else {
          line = test;
        }
      }
      if (line) out.push(line);
    }
    ctx.restore();
    return out;
  }

  /**
   * Arrow + label overlay drawn on the deployment grid (used by the
   * `arrowHint` tutorial step). Coords are battlefield cell space.
   */
  private _storyArrow(g: Game): void {
    const hint = g.storyController?.arrowHint;
    if (!hint) return;
    const cx1 = OXB + hint.from.x * CB + CB / 2;
    const cy1 = OYB + hint.from.y * CB + CB / 2;
    const cx2 = OXB + hint.to.x * CB + CB / 2;
    const cy2 = OYB + hint.to.y * CB + CB / 2;
    // Pulsing yellow arrow
    const pulse = 0.6 + 0.4 * Math.sin(Date.now() / 200);
    ctx.save();
    ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(cx1, cy1);
    ctx.lineTo(cx2, cy2);
    ctx.stroke();
    // Arrowhead at destination
    const ang = Math.atan2(cy2 - cy1, cx2 - cx1);
    const headLen = 14;
    ctx.beginPath();
    ctx.moveTo(cx2, cy2);
    ctx.lineTo(cx2 - Math.cos(ang - 0.4) * headLen, cy2 - Math.sin(ang - 0.4) * headLen);
    ctx.lineTo(cx2 - Math.cos(ang + 0.4) * headLen, cy2 - Math.sin(ang + 0.4) * headLen);
    ctx.closePath();
    ctx.fillStyle = `rgba(255,204,102,${pulse})`;
    ctx.fill();
    // Label above the destination
    if (hint.label) {
      ctx.fillStyle = '#ffcc66';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
      ctx.fillText(hint.label, cx2, cy2 - 14);
    }
    ctx.restore();
  }

  /**
   * Story Mode deploy-phase highlights:
   *   - expectedPlace: pulsing yellow ring on the target cell
   *   - expectedPath: dotted yellow path through expected cells + numbers
   *   - expectedStartBattle: glow around the Start Battle button
   * All three are show-when-set; no constraint = no overlay.
   */
  private _storyDeployHints(g: Game): void {
    const ctrl = g.storyController;
    if (!ctrl) return;
    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 220);
    ctx.save();
    // Place hint — full-cell pulsing border on target grid cell + the
    // matching palette slot below (so the player knows which unit to
    // drag from, not just where to drop it).
    if (ctrl.expectedPlace) {
      // (a) Target grid cell highlight
      const p = ctrl.expectedPlace.hintPos;
      const px = OXB + p.x * CB;
      const py = OYB + p.y * CB;
      ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(px + 1.5, py + 1.5, CB - 3, CB - 3);
      ctx.fillStyle = `rgba(255,204,102,${pulse * 0.15})`;
      ctx.fillRect(px + 2, py + 2, CB - 4, CB - 4);
      if (ctrl.expectedPlace.hint) {
        ctx.fillStyle = '#ffcc66';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        ctx.fillText(ctrl.expectedPlace.hint, px + CB / 2, py - 2);
      }
      // (b) Palette slot highlight — geometry mirrors `_deployPanel`
      // palette loop: palY=ZMY+10, palH=52, palX=10, palW=(W-20)/N.
      // Slot rect inside loop is (sx+2, palY, palW-4, palH).
      const palette = g.deployment.palette;
      const paletteIdx = palette.findIndex((s) => s.type === ctrl.expectedPlace!.type);
      if (paletteIdx >= 0) {
        const palY = ZMY + 10;
        const palH = 52;
        const palX = 10;
        const palW = (W - 20) / palette.length;
        const sx = palX + paletteIdx * palW;
        ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(sx + 2 - 2, palY - 2, palW - 4 + 4, palH + 4);
      }
    }
    // Path hint — draw each REMAINING expected cell (already-drawn cells
    // are already part of the unit's path which Renderer.deployGrid
    // renders normally). Player sees a sequence of numbered dotted
    // targets to walk through.
    if (ctrl.expectedPath) {
      const unitIdx = g.deployment.unitAt(ctrl.expectedPath.unitPos.x, ctrl.expectedPath.unitPos.y);
      const currentLen = unitIdx >= 0 ? g.deployment.placed[unitIdx].path.length : 0;
      const cells = ctrl.expectedPath.cells;
      for (let i = currentLen; i < cells.length; i++) {
        const c = cells[i];
        const px = OXB + c.x * CB;
        const py = OYB + c.y * CB;
        // The NEXT cell pulses brighter, later cells stay faint.
        const isNext = i === currentLen;
        const alpha = isNext ? pulse : 0.35;
        ctx.strokeStyle = `rgba(255,204,102,${alpha})`;
        ctx.lineWidth = isNext ? 3 : 2;
        ctx.setLineDash(isNext ? [] : [4, 3]);
        ctx.strokeRect(px + 1.5, py + 1.5, CB - 3, CB - 3);
        // Number badge in the corner
        ctx.fillStyle = isNext ? '#ffcc66' : 'rgba(255,204,102,0.6)';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        ctx.fillText(`${i + 1}`, px + 4, py + 3);
      }
      ctx.setLineDash([]);
    }
    // Start Battle button glow — surrounds Row A right rect (only when
    // canConfirm and the button is visible).
    if (ctrl.expectedStartBattle && g.deployment.canConfirm()) {
      const btnH = 38;
      const baseY = ZMY + 72;
      const dpW = W * 0.36;
      const cfX = W * 0.04 + dpW + 8;
      const cfW = W - cfX - W * 0.04;
      ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(cfX - 2, baseY - 2, cfW + 4, btnH + 4);
    }
    // "Draw Paths" toggle glow — Row A left rect. Geometry mirrors
    // Input._getDeployButtons Row A left.
    if (ctrl.expectedPathToggle) {
      const btnH = 38;
      const baseY = ZMY + 72;
      const dpW = W * 0.36;
      ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(W * 0.04 - 2, baseY - 2, dpW + 4, btnH + 4);
    }
    ctx.restore();
  }

  /**
   * Story Mode SCHED-phase highlights:
   *   - expectedSpell.targetPos != null: pulsing target cell on the battlefield
   *   - expectedConfirmSched: glow around the Confirm Plan button
   * Element-of-spell highlight on the bottom list isn't drawn here — the
   * dialogue line tells the player which element to pick, and `selectSpell`
   * rejects wrong picks silently.
   */
  private _storySchedHints(g: Game): void {
    const ctrl = g.storyController!;
    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 220);
    ctx.save();
    // Spell-button highlight — when the tutorial demands a specific
    // element (expectedSpell.el !== -1) AND we're at the slot step
    // (player hasn't picked yet), highlight the matching row in the
    // spell list. Geometry mirrors `_scheduling`'s 'slot' branch.
    if (ctrl.expectedSpell && ctrl.expectedSpell.el !== -1
        && ctrl.expectedSpell.slot === g.spellSlot
        && g.schedStep === 'slot' && !g.targeting) {
      const slotsBottom = ZMY + 78;
      const bodyTop = slotsBottom + 12;
      const listY = g.spellSlot > 0 ? bodyTop + 28 + 10 : bodyTop;
      const idx = g.availSpells.findIndex((s) => s.el === ctrl.expectedSpell!.el);
      if (idx >= 0) {
        const by = listY + idx * 42;
        ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(W * 0.08 - 2, by - 2, W * 0.84 + 4, 36 + 4);
      }
    }
    // Target cell highlight — pulsing ring on the battlefield cell the
    // tutorial wants the player to target. Shown when the constraint
    // has a specific targetPos AND the player is in the targeting step.
    if (ctrl.expectedSpell && ctrl.expectedSpell.targetPos && g.targeting) {
      const t = ctrl.expectedSpell.targetPos;
      const px = OXB + t.x * CB;
      const py = OYB + t.y * CB;
      ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(px + 1.5, py + 1.5, CB - 3, CB - 3);
      ctx.fillStyle = `rgba(255,204,102,${pulse * 0.18})`;
      ctx.fillRect(px + 2, py + 2, CB - 4, CB - 4);
      if (ctrl.expectedSpell.hint) {
        ctx.fillStyle = '#ffcc66';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        ctx.fillText(ctrl.expectedSpell.hint, px + CB / 2, py - 2);
      }
    }
    // Confirm Plan button glow — drawn over the SCHED bottom panel.
    // Layout mirrors _scheduling 'confirm' step in Renderer.
    if (ctrl.expectedConfirmSched && g.schedStep === 'confirm') {
      const slotsBottom = ZMY + 78;
      const bodyTop = slotsBottom + 12;
      const rowY = bodyTop + 8;
      const rowH = 48;
      const padX = W * 0.05;
      const totalW = W - padX * 2;
      const gapX = 10;
      const backW = totalW * 0.30;
      const confirmW = totalW - backW - gapX;
      const cx = padX + backW + gapX;
      ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(cx - 2, rowY - 2, confirmW + 4, rowH + 4);
    }
    ctx.restore();
  }

  /**
   * Highlight overlay drawn on the M3 board during a forced-swap step.
   * Outlines the two cells the player must swap.
   */
  private _storySwapHighlight(g: Game): void {
    const sw = g.storyController?.expectedSwap;
    if (!sw) return;
    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 200);
    ctx.save();
    ctx.strokeStyle = `rgba(255,204,102,${pulse})`;
    ctx.lineWidth = 3;
    for (const cell of [[sw.r1, sw.c1], [sw.r2, sw.c2]]) {
      const [r, c] = cell;
      const x = OXM + c * CM;
      const y = OYM + r * CM;
      ctx.strokeRect(x + 1.5, y + 1.5, CM - 3, CM - 3);
    }
    // Connecting line between the two cells (swap direction hint)
    const x1 = OXM + sw.c1 * CM + CM / 2;
    const y1 = OYM + sw.r1 * CM + CM / 2;
    const x2 = OXM + sw.c2 * CM + CM / 2;
    const y2 = OYM + sw.r2 * CM + CM / 2;
    ctx.strokeStyle = `rgba(255,204,102,${pulse * 0.7})`;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Hint label above
    if (sw.hint) {
      ctx.fillStyle = '#ffcc66';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
      ctx.fillText(sw.hint, (x1 + x2) / 2, Math.min(y1, y2) - CM * 0.55);
    }
    ctx.restore();
  }

  private _deploy(g: Game): void {
    const dep = g.deployment;

    // Draw battlefield grid (top portion)
    this._deployGrid(g, dep);

    // Draw bottom panel (palette / buttons)
    this._deployPanel(g, dep);

    // Draw drag ghost (palette → grid)
    if (dep.dragging && dep.dragPos && dep.dragType) {
      const cfg = UCFG[dep.dragType];
      const px = dep.dragPos.x, py = dep.dragPos.y;
      const r = CB * 0.36;
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = '#4488ff';
      this._drawUnitShape(cfg.shape, px, py, r);
      ctx.globalAlpha = 1;
    }

    // Draw place-mode grid drag ghost (reposition or remove)
    if (dep.dragRemoveIdx >= 0 && dep.dragPos) {
      const u = dep.placed[dep.dragRemoveIdx];
      if (u) {
        const cfg = UCFG[u.type];
        const px = dep.dragPos.x, py = dep.dragPos.y;
        const r = CB * 0.36;
        const inRemoveZone = py >= ZMY;

        // Determine reposition target & validity
        let tCol = -1, tRow = -1, tValid = false, tInvalid = false;
        if (!inRemoveZone && py >= OYB && py < OYB + BATTLE_ROWS * CB) {
          tCol = Math.floor((px - OXB) / CB);
          tRow = Math.floor((py - OYB) / CB);
          if (tCol >= 0 && tCol < BATTLE_COLS && tRow >= 0 && tRow < BATTLE_ROWS) {
            const isOrigin = (tCol === u.pos.x && tRow === u.pos.y);
            if (isOrigin) { /* neutral, no highlight */ }
            else if (dep.isPlayerCell(tCol, tRow) && !dep.isOccupied(tCol, tRow)) tValid = true;
            else tInvalid = true;
          }
        }

        // Highlight target cell
        if (tValid) {
          ctx.fillStyle = 'rgba(68,255,136,0.18)';
          ctx.fillRect(OXB + tCol * CB, OYB + tRow * CB, CB, CB);
          ctx.strokeStyle = '#44ff88'; ctx.lineWidth = 2;
          ctx.strokeRect(OXB + tCol * CB + 2, OYB + tRow * CB + 2, CB - 4, CB - 4);
        } else if (tInvalid) {
          ctx.fillStyle = 'rgba(255,68,85,0.15)';
          ctx.fillRect(OXB + tCol * CB, OYB + tRow * CB, CB, CB);
        }

        // Ghost color reflects intent
        let ghostCol = '#4488ff', ghostA = 0.7;
        if (inRemoveZone)      { ghostCol = '#ff4455'; ghostA = 0.45; }
        else if (tValid)       { ghostCol = '#44ff88'; ghostA = 0.85; }
        else if (tInvalid)     { ghostCol = '#aa3333'; ghostA = 0.5; }

        ctx.globalAlpha = ghostA;
        ctx.fillStyle = ghostCol;
        this._drawUnitShape(cfg.shape, px, py, r);
        // Label
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${Math.max(9, CB * 0.26)}px sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(u.type[0], px, py);
        ctx.globalAlpha = 1;

        // Bottom zone tint + hint when about to remove
        if (inRemoveZone) {
          ctx.fillStyle = 'rgba(255,68,85,0.3)';
          ctx.fillRect(0, ZMY, W, H - ZMY);
          ctx.fillStyle = '#ff6666'; ctx.font = 'bold 14px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('Release to remove', W / 2, ZMY + (H - ZMY) / 2);
        }
      }
    }
  }

  /** Draw the 8x8 grid during deployment */
  private _deployGrid(g: Game, dep: Deployment): void {
    // Grid lines
    ctx.strokeStyle = 'rgba(40,60,100,0.35)';
    ctx.lineWidth = 0.5;
    for (let r = 0; r <= BATTLE_ROWS; r++) {
      ctx.beginPath(); ctx.moveTo(OXB, OYB + r * CB); ctx.lineTo(OXB + BATTLE_COLS * CB, OYB + r * CB); ctx.stroke();
    }
    for (let c = 0; c <= BATTLE_COLS; c++) {
      ctx.beginPath(); ctx.moveTo(OXB + c * CB, OYB); ctx.lineTo(OXB + c * CB, OYB + BATTLE_ROWS * CB); ctx.stroke();
    }

    // Highlight player placeable zone (always view rows 6-7 — that's the
    // bottom of YOUR view regardless of which team you are).
    ctx.fillStyle = 'rgba(68,136,255,0.08)';
    for (let r = 6; r <= 7; r++) {
      for (let c = 0; c < BATTLE_COLS; c++) {
        if (dep.isPlayerCell(c, r)) {
          ctx.fillRect(OXB + c * CB, OYB + r * CB, CB, CB);
        }
      }
    }

    // Terrain — drawn during DEPLOY so the player can plan paths around
    // grass / puddle. Always under bases / units / paths.
    if (g.terrain) this._terrainLayer(g.terrain);

    // Draw base tiles. Coords are WORLD; flip to view for guest.
    const baseTiles: { x: number; y: number; team: number }[] = [
      { x: 3, y: 7, team: 0 }, { x: 4, y: 7, team: 0 },
      { x: 3, y: 0, team: 1 }, { x: 4, y: 0, team: 1 },
    ];
    for (const bt of baseTiles) {
      const col = bt.team === g.myTeam ? 'rgba(68,136,255,' : 'rgba(255,68,85,';
      const vx = g.viewFlipX(bt.x), vy = g.viewFlipY(bt.y);
      const bx = OXB + vx * CB, by = OYB + vy * CB;
      ctx.fillStyle = col + '0.25)';
      ctx.fillRect(bx + 1, by + 1, CB - 2, CB - 2);
      ctx.strokeStyle = col + '0.7)'; ctx.lineWidth = 2;
      ctx.strokeRect(bx + 1, by + 1, CB - 2, CB - 2);
      // Castle icon
      const cx = bx + CB / 2, cy = by + CB / 2;
      const s = CB * 0.22;
      ctx.fillStyle = col + '0.8)';
      ctx.fillRect(cx - s * 1.2, cy - s * 0.5, s * 2.4, s * 1.4);
      ctx.fillRect(cx - s * 1.4, cy - s * 1.1, s * 0.5, s * 0.7);
      ctx.fillRect(cx + s * 0.9, cy - s * 1.1, s * 0.5, s * 0.7);
      ctx.fillRect(cx - s * 0.25, cy - s * 1.3, s * 0.5, s * 0.9);
    }

    // Draw placed units (skip unit being drag-removed)
    for (let i = 0; i < dep.placed.length; i++) {
      if (i === dep.dragRemoveIdx) continue; // drawn as ghost instead
      const u = dep.placed[i];
      const cfg = UCFG[u.type];
      const px = OXB + u.pos.x * CB + CB / 2;
      const py = OYB + u.pos.y * CB + CB / 2;
      const r = CB * 0.36;
      const isSelected = i === dep.selectedUnit;

      // Selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#ffdd44'; ctx.lineWidth = 2;
        ctx.strokeRect(OXB + u.pos.x * CB + 2, OYB + u.pos.y * CB + 2, CB - 4, CB - 4);
      }

      // Unit icon (Session 7: thematic — sword/horseshoe/bow/wand)
      ctx.fillStyle = '#4488ff';
      this._drawUnitShape(cfg.shape, px, py, r);

      // Draw path preview
      if (u.path.length > 0) {
        ctx.strokeStyle = isSelected ? 'rgba(255,221,68,0.6)' : 'rgba(68,136,255,0.3)';
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(px, py);
        for (const p of u.path) {
          ctx.lineTo(OXB + p.x * CB + CB / 2, OYB + p.y * CB + CB / 2);
        }
        ctx.stroke();
        ctx.setLineDash([]);

        // Path end marker
        const last = u.path[u.path.length - 1];
        ctx.fillStyle = isSelected ? 'rgba(255,221,68,0.4)' : 'rgba(68,136,255,0.2)';
        ctx.beginPath();
        ctx.arc(OXB + last.x * CB + CB / 2, OYB + last.y * CB + CB / 2, 4, 0, Math.PI * 2);
        ctx.fill();

        // Path length indicator
        if (isSelected) {
          ctx.fillStyle = '#ffdd44';
          ctx.font = `${Math.max(7, CB * 0.18)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(`${u.path.length}/${cfg.maxPath}`, px, py + r + 8);
        }
      }
    }

    // Title bar — content depends on mode
    ctx.fillStyle = '#0e1328'; ctx.fillRect(0, 0, W, OYB - 2);
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const selU = dep.selectedUnit >= 0 ? dep.placed[dep.selectedUnit] : null;
    let title: string;
    if (dep.pathMode) {
      ctx.fillStyle = '#88ffbb';
      title = selU
        ? `Path Mode · ${selU.type} ${selU.path.length}/${UCFG[selU.type].maxPath} (tap again to redraw)`
        : 'Path Mode · tap a unit to plan its route';
    } else {
      ctx.fillStyle = '#ffdd44';
      title = selU
        ? `${selU.type} selected · drag to move, drag down to remove`
        : 'Deploy Units';
    }
    ctx.fillText(title, W / 2, (OYB - 2) / 2);

    if (g.deployDeadline) {
      const remain = Math.max(0, Math.ceil((g.deployDeadline - Date.now()) / 1000));
      ctx.fillStyle = remain <= 10 ? '#ff4444' : '#44ff88';
      ctx.font = 'bold 16px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${remain}s`, W - 10, (OYB - 2) / 2);
    }
  }

  /** Draw deployment bottom panel.
   *
   *  Branches on `dep.rules.kind` (single source of truth for UI mode):
   *   - 'manual' (single / PvP): full panel — palette + Draw Paths toggle
   *      + Start + save/load slots + Remove + Main Menu back button.
   *   - 'preset' (Story / Casual): simplified panel — Path Mode toggle +
   *      Start + Back button. Units are pre-placed by the script so the
   *      placement-related UI is hidden entirely.
   */
  private _deployPanel(g: Game, dep: Deployment): void {
    // Background (shared by both layouts)
    ctx.fillStyle = '#0e1328';
    ctx.fillRect(0, ZMY, W, H - ZMY);
    ctx.strokeStyle = '#1a2040'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, ZMY); ctx.lineTo(W, ZMY); ctx.stroke();

    if (dep.rules.kind === 'preset') {
      this._deployPanelPreset(g, dep);
      return;
    }

    // ----- Manual mode panel (single / PvP) -----
    // Palette always visible; path info is surfaced in the top title bar.

    // Palette slots
    const palY = ZMY + 10;
    const palH = 52;
    const palX = 10;
    const palW = (W - 20) / dep.palette.length;

    for (let i = 0; i < dep.palette.length; i++) {
      const slot = dep.palette[i];
      const sx = palX + i * palW;
      const cfg = UCFG[slot.type];
      const remaining = slot.max - slot.placed;
      const available = remaining > 0;

      ctx.fillStyle = available ? '#1a2545' : '#111830';
      ctx.fillRect(sx + 2, palY, palW - 4, palH);
      ctx.strokeStyle = i === dep.selectedPalette ? '#ffdd44' : (available ? '#335588' : '#222');
      ctx.lineWidth = i === dep.selectedPalette ? 2 : 1;
      ctx.strokeRect(sx + 2, palY, palW - 4, palH);

      const ux = sx + palW / 2, uy = palY + 20;
      const r = 12;
      ctx.fillStyle = available ? '#4488ff' : '#333';
      this._drawUnitShape(cfg.shape, ux, uy, r);
      // Session 7: drop type-initial overlay — new thematic icon already
      // conveys unit type; the 3-letter label below repeats it for newbies.

      // Session 8: drop the 3-letter type label (Inf/Cav/Arc) — overlapped
      // with the count below it. The thematic icon already conveys type;
      // count alone is enough.
      // Session 9: shrink the count font (was palW*0.13 ≈ 26px on desktop,
      // way too dominant). Keep a sane mobile minimum.
      ctx.fillStyle = available ? '#aabbdd' : '#555';
      ctx.font = `bold ${Math.max(10, Math.min(13, palW * 0.10))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${remaining}/${slot.max}`, sx + palW / 2, palY + 44);
    }

    // --- Row L (Session 9): Leader picker — one tile per LEADER entry,
    //     selected pick highlighted. Sits between palette and Row A; all
    //     downstream rows shift by leaderH + 4 so Input._getDeployButtons
    //     stays aligned (it computes baseY from the same constant).
    //
    // Single-mode revision: player also picks the AI's leader — show a
    // second row labeled "Opp" below the "You" row. PvP / preset modes
    // hide the AI row (PvP opponent reports their own pick; preset
    // modes use the default). The Row-A baseY shift below mirrors this
    // — see also Input._getDeployButtons (same constants). ---
    const leaderY = ZMY + 66;
    const leaderH = 22;
    const leaderIds = Object.keys(LEADERS);
    const ldrGap = 4;
    const ldrW = (W - W * 0.08 - ldrGap * (leaderIds.length - 1)) / leaderIds.length;
    const showAiPicker = g.gameMode === 'single';
    const aiLeaderY = leaderY + leaderH + 4;

    const drawLeaderRow = (rowY: number, currentId: string, label: string, isAi: boolean) => {
      // Small text label on the LEFT — sits just above the row so it
      // doesn't take horizontal space away from the leader tiles.
      ctx.fillStyle = '#8899aa'; ctx.font = '10px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText(label, W * 0.04, rowY - 7);
      for (let i = 0; i < leaderIds.length; i++) {
        const id = leaderIds[i];
        const lx = W * 0.04 + i * (ldrW + ldrGap);
        const selected = currentId === id;
        // AI row uses a slightly cooler highlight so the player can tell
        // their own selection apart at a glance.
        ctx.fillStyle = selected ? (isAi ? '#2a1d3a' : '#1d2a48') : '#0e1828';
        ctx.fillRect(lx, rowY, ldrW, leaderH);
        ctx.strokeStyle = selected ? (isAi ? '#cc88ff' : '#ffd24a') : '#335577';
        ctx.lineWidth = selected ? 2 : 1;
        ctx.strokeRect(lx, rowY, ldrW, leaderH);
        ctx.fillStyle = selected ? (isAi ? '#cc88ff' : '#ffd24a') : '#aaccee';
        ctx.font = selected ? 'bold 12px sans-serif' : '12px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(id, lx + ldrW / 2, rowY + leaderH / 2 + 1);
      }
    };

    drawLeaderRow(leaderY, dep.leader, 'You', false);
    if (showAiPicker) drawLeaderRow(aiLeaderY, dep.aiLeader, 'Opp', true);

    // --- Row A: [Draw Paths toggle (40%)] [Confirm / placeholder (60%)] ---
    // Single mode adds an extra leader row → push Row A down by another
    // (leaderH + 4) = 26px. Non-single keeps the original ZMY+96.
    const btnY = showAiPicker ? ZMY + 122 : ZMY + 96;
    const btnH = 38;
    const dpW = W * 0.36;
    const cfX = W * 0.04 + dpW + 8;
    const cfW = W - cfX - W * 0.04;

    // Draw Paths toggle
    const pmOn = dep.pathMode;
    ctx.fillStyle = pmOn ? '#15301f' : '#0e1828';
    ctx.fillRect(W * 0.04, btnY, dpW, btnH);
    ctx.strokeStyle = pmOn ? '#88ffbb' : '#3a5577';
    ctx.lineWidth = pmOn ? 2 : 1;
    ctx.strokeRect(W * 0.04, btnY, dpW, btnH);
    ctx.fillStyle = pmOn ? '#aaffcc' : '#88aacc';
    ctx.font = pmOn ? 'bold 13px sans-serif' : '13px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(pmOn ? '✏ Drawing Paths' : 'Draw Paths', W * 0.04 + dpW / 2, btnY + btnH / 2 + 1);

    // Confirm / Start Battle (or placeholder when nothing placed)
    if (dep.canConfirm()) {
      ctx.fillStyle = '#1a3520'; ctx.fillRect(cfX, btnY, cfW, btnH);
      ctx.strokeStyle = '#44ff88'; ctx.lineWidth = 1.5; ctx.strokeRect(cfX, btnY, cfW, btnH);
      ctx.fillStyle = '#44ff88'; ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Start Battle!', cfX + cfW / 2, btnY + btnH / 2 + 1);
    } else {
      ctx.fillStyle = 'rgba(100,140,220,0.18)'; ctx.fillRect(cfX, btnY, cfW, btnH);
      ctx.strokeStyle = 'rgba(100,140,220,0.25)'; ctx.lineWidth = 1; ctx.strokeRect(cfX, btnY, cfW, btnH);
      ctx.fillStyle = 'rgba(170,190,220,0.6)'; ctx.font = '12px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Place a unit to start', cfX + cfW / 2, btnY + btnH / 2 + 1);
    }

    // --- Row B: Save/Load slot row (3 tiles) ---
    const sSlotY = btnY + btnH + 8;          // ZMY + 118
    const sSlotH = 30;
    const sSlotGap = 4;
    const sSlotW = (W * 0.92 - sSlotGap * 2) / 3;
    const slots = g.listSlots();
    const action = g.slotAction;

    for (let i = 0; i < 3; i++) {
      const sx = W * 0.04 + i * (sSlotW + sSlotGap);
      const data = slots[i];
      const filled = data !== null;
      let bg = '#101830', border = '#2a3858', fg = '#556688';
      if (filled) { bg = '#132035'; border = '#4488bb'; fg = '#aaccee'; }
      if (action === 'save')  { border = '#44ff88'; bg = '#0e2015'; }
      if (action === 'clear') { border = '#ff6644'; bg = '#200e0e'; }
      ctx.fillStyle = bg; ctx.fillRect(sx, sSlotY, sSlotW, sSlotH);
      ctx.strokeStyle = border; ctx.lineWidth = 1.2;
      ctx.strokeRect(sx, sSlotY, sSlotW, sSlotH);
      ctx.fillStyle = fg;
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const label = filled
        ? `Slot ${i + 1}  ·  ${data!.units.length}u`
        : `Slot ${i + 1}  ·  —`;
      ctx.fillText(label, sx + sSlotW / 2, sSlotY + sSlotH / 2 + 1);
    }

    // --- Row C: Save / Clear toggle ---
    const actY = sSlotY + sSlotH + 6;        // ZMY + 154
    const actH = 30;
    const actW = W * 0.44;

    const saveOn = action === 'save';
    ctx.fillStyle = saveOn ? '#14301c' : '#0e1828';
    ctx.fillRect(W * 0.04, actY, actW, actH);
    ctx.strokeStyle = saveOn ? '#44ff88' : '#335566'; ctx.lineWidth = saveOn ? 2 : 1;
    ctx.strokeRect(W * 0.04, actY, actW, actH);
    ctx.fillStyle = saveOn ? '#88ffbb' : '#88bbdd';
    ctx.font = saveOn ? 'bold 12px sans-serif' : '12px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(saveOn ? 'Tap slot to SAVE' : '↓ Save to slot', W * 0.04 + actW / 2, actY + actH / 2 + 1);

    const clrOn = action === 'clear';
    ctx.fillStyle = clrOn ? '#2a1016' : '#0e1828';
    ctx.fillRect(W * 0.52, actY, actW, actH);
    ctx.strokeStyle = clrOn ? '#ff6644' : '#335566'; ctx.lineWidth = clrOn ? 2 : 1;
    ctx.strokeRect(W * 0.52, actY, actW, actH);
    ctx.fillStyle = clrOn ? '#ffaa88' : '#bb7788';
    ctx.font = clrOn ? 'bold 12px sans-serif' : '12px sans-serif';
    ctx.fillText(clrOn ? 'Tap slot to CLEAR' : '🗑 Clear slot', W * 0.52 + actW / 2, actY + actH / 2 + 1);

    // --- Row D: Remove selected unit (conditional) ---
    if (dep.selectedUnit >= 0) {
      const ry = actY + actH + 8;            // ZMY + 192
      ctx.fillStyle = '#2a1520'; ctx.fillRect(W * 0.04, ry, W * 0.92, btnH);
      ctx.strokeStyle = '#ff4455'; ctx.lineWidth = 1; ctx.strokeRect(W * 0.04, ry, W * 0.92, btnH);
      ctx.fillStyle = '#ff6666'; ctx.font = '13px sans-serif';
      ctx.textAlign = 'center';
      const su = dep.placed[dep.selectedUnit];
      ctx.fillText(`Remove ${su.type}`, W / 2, ry + btnH / 2 + 1);
    }

    // --- Row E: Back to Main Menu (single + story, not PvP) ---
    // PvP players already have cancel via the LOBBY screen; single
    // needs a way back because MENU → DEPLOY is direct with no LOBBY.
    // (Story / Casual go through the preset panel which has its own
    // Back button; never reach this branch.)
    if (g.gameMode === 'single') {
      // Place below Row D (or below Row C if no unit selected).
      const backY = dep.selectedUnit >= 0
        ? actY + actH + 8 + btnH + 8   // below Row D
        : actY + actH + 8;             // below Row C
      const backH = 30;
      ctx.fillStyle = 'rgba(20,30,55,0.6)';
      ctx.fillRect(W * 0.25, backY, W * 0.50, backH);
      ctx.strokeStyle = '#556677'; ctx.lineWidth = 1;
      ctx.strokeRect(W * 0.25, backY, W * 0.50, backH);
      ctx.fillStyle = '#8899aa'; ctx.font = '12px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('← Main Menu', W / 2, backY + backH / 2);
    }

    // Bottom hint — depends on mode + selection
    ctx.fillStyle = 'rgba(100,140,220,0.4)'; ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    let hint: string;
    if (dep.pathMode) {
      hint = dep.selectedUnit >= 0
        ? 'Drag from unit to draw path · tap unit again to reset'
        : 'Tap a placed unit to start drawing its path';
    } else {
      hint = dep.selectedUnit >= 0
        ? 'Drag unit to a new cell · drag down to remove'
        : 'Drag palette onto blue zone · drag placed units to move/remove';
    }
    ctx.fillText(hint, W / 2, H - 12);
  }

  /**
   * Preset-mode deployment panel — Story / Casual share this layout.
   *
   * Hidden vs manual panel (single / PvP):
   *   - Palette row (units pre-placed by script, no add/remove)
   *   - Save/Load slot row + Save/Clear toggles (script is authority)
   *   - Remove selected unit button (script-placed units are locked)
   *
   * Kept:
   *   - Path Mode toggle + Start Battle button (Row A — same geometry
   *     as manual so Input._getDeployButtons can share Row A hit-rect)
   *   - Back button at the bottom (text + target varies by mode)
   *
   * Layout: Row A sits at the SAME btnY as the manual panel (`ZMY + 72`)
   * so hit-rects in Input.ts stay byte-identical.
   */
  private _deployPanelPreset(g: Game, dep: Deployment): void {
    // Header row — level / chapter title for context.
    // Casual reads from `g.casualLevel`; Story reads from
    // `g.storyController.chapter`. No-op if neither is set.
    const title = g.casualLevel?.title ?? g.storyController?.chapter?.title ?? null;
    const subtitle = g.casualLevel?.subtitle ?? g.storyController?.chapter?.subtitle ?? null;
    if (title) {
      ctx.fillStyle = '#66ddcc';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(title, W / 2, ZMY + 18);
      if (subtitle) {
        ctx.fillStyle = '#88bbb0';
        ctx.font = '11px sans-serif';
        ctx.fillText(subtitle, W / 2, ZMY + 38);
      }
    }

    // --- Row A (same geometry as manual): Draw Paths toggle + Start ---
    const btnY = ZMY + 72;
    const btnH = 38;
    const dpW = W * 0.36;
    const cfX = W * 0.04 + dpW + 8;
    const cfW = W - cfX - W * 0.04;

    // Draw Paths toggle
    const pmOn = dep.pathMode;
    ctx.fillStyle = pmOn ? '#15301f' : '#0e1828';
    ctx.fillRect(W * 0.04, btnY, dpW, btnH);
    ctx.strokeStyle = pmOn ? '#88ffbb' : '#3a5577';
    ctx.lineWidth = pmOn ? 2 : 1;
    ctx.strokeRect(W * 0.04, btnY, dpW, btnH);
    ctx.fillStyle = pmOn ? '#aaffcc' : '#88aacc';
    ctx.font = pmOn ? 'bold 13px sans-serif' : '13px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(pmOn ? '✏ Drawing Paths' : 'Draw Paths', W * 0.04 + dpW / 2, btnY + btnH / 2 + 1);

    // Start Battle button — preset modes always have units placed so
    // canConfirm() is always true. Story mode may additionally gate
    // this via expectStartBattle (handled at the action layer); Renderer
    // just shows it active.
    ctx.fillStyle = '#1a3520'; ctx.fillRect(cfX, btnY, cfW, btnH);
    ctx.strokeStyle = '#44ff88'; ctx.lineWidth = 1.5; ctx.strokeRect(cfX, btnY, cfW, btnH);
    ctx.fillStyle = '#44ff88'; ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('Start Battle!', cfX + cfW / 2, btnY + btnH / 2 + 1);

    // --- Back button --- (label + target chosen by gameMode)
    //   Casual : "Back to Casual Select" → exitToCasualSelect
    //   Story  : "Back to Main Menu"     → restart (the lone way out)
    const backY = btnY + btnH + 18;
    const backH = 32;
    const isCasual = g.gameMode === 'casual';
    const backLabel = isCasual ? '← Back to Casual Select' : '← Back to Main Menu';
    ctx.fillStyle = 'rgba(34,52,60,0.7)';
    ctx.fillRect(W * 0.20, backY, W * 0.60, backH);
    ctx.strokeStyle = '#66ddcc'; ctx.lineWidth = 1.5;
    ctx.strokeRect(W * 0.20, backY, W * 0.60, backH);
    ctx.fillStyle = '#aaeedd'; ctx.font = '13px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(backLabel, W / 2, backY + backH / 2);

    // Bottom hint — casual-specific wording.
    ctx.fillStyle = 'rgba(102,221,204,0.4)'; ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    const hint = dep.pathMode
      ? (dep.selectedUnit >= 0
          ? 'Drag from unit to draw path · tap unit again to reset'
          : 'Tap a placed unit to start drawing its path')
      : 'Tap a unit to select · turn on Draw Paths to plan the route';
    ctx.fillText(hint, W / 2, H - 12);
  }

  /**
   * Draw a unit-type icon at (px,py). Session 7 (2026-05-06): replaced
   * the old generic primitives (square/diamond/triangle/circle) with
   * thematic icons drawn in the same rect+arc primitive style as the
   * base castle icon — sword (Infantry), horseshoe (Cavalry), bow
   * (Archer), wand (Mage, kept for re-enable).
   *
   * Caller contract: set `ctx.fillStyle` to the team color before
   * calling. The function uses fillStyle as the icon color and also
   * borrows it for stroke strokes via ctx.strokeStyle = fillStyle.
   * Each branch saves/restores ctx state when it modifies lineWidth /
   * transform so callers don't leak state.
   */
  private _drawUnitShape(shape: string, px: number, py: number, r: number): void {
    const col = ctx.fillStyle as string;
    ctx.save();
    ctx.strokeStyle = col;

    if (shape === 'sword') {
      // Blade: thin vertical bar, top half of icon
      const bw = r * 0.30, bh = r * 1.30;
      ctx.fillRect(px - bw / 2, py - r * 0.85, bw, bh);
      // Cross-guard
      const gw = r * 1.10, gh = r * 0.20;
      ctx.fillRect(px - gw / 2, py + r * 0.30, gw, gh);
      // Grip
      const hw = r * 0.26, hh = r * 0.40;
      ctx.fillRect(px - hw / 2, py + r * 0.50, hw, hh);
      // Pommel
      const pw = r * 0.42, ph = r * 0.18;
      ctx.fillRect(px - pw / 2, py + r * 0.92, pw, ph);
    } else if (shape === 'horseshoe') {
      // Realistic Horseshoe (U-shape wider in the middle, slightly narrower at ends)
      ctx.lineWidth = Math.max(3, r * 0.35);
      ctx.lineCap = 'round';
      ctx.beginPath();
      // Left tip
      ctx.moveTo(px - r * 0.5, py + r * 0.8);
      // Curve up to top
      ctx.bezierCurveTo(
        px - r * 1.0, py - r * 0.1, 
        px - r * 0.6, py - r * 0.9, 
        px, py - r * 0.9
      );
      // Curve down to right tip
      ctx.bezierCurveTo(
        px + r * 0.6, py - r * 0.9, 
        px + r * 1.0, py - r * 0.1, 
        px + r * 0.5, py + r * 0.8
      );
      ctx.stroke();

      // Calkins (small blocks at the ends of the horseshoe)
      ctx.fillRect(px - r * 0.65, py + r * 0.7, r * 0.3, r * 0.2);
      ctx.fillRect(px + r * 0.35, py + r * 0.7, r * 0.3, r * 0.2);

      // Removed nail holes per user request
    } else if (shape === 'bow') {
      // Recurve Bow and drawn arrow
      // Bow Body
      ctx.lineWidth = Math.max(2, r * 0.15);
      ctx.lineCap = 'round';
      ctx.beginPath();
      // Top tip
      ctx.moveTo(px - r * 0.3, py - r * 0.9);
      // Curve to handle
      ctx.bezierCurveTo(
        px + r * 0.4, py - r * 0.8,
        px + r * 0.6, py - r * 0.3,
        px + r * 0.5, py
      );
      // Curve to bottom tip
      ctx.bezierCurveTo(
        px + r * 0.6, py + r * 0.3,
        px + r * 0.4, py + r * 0.8,
        px - r * 0.3, py + r * 0.9
      );
      ctx.stroke();


      // Bowstring (pulled back)
      ctx.lineWidth = Math.max(1, r * 0.05);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.3, py - r * 0.9);
      ctx.lineTo(px - r * 0.7, py); // drawn to left
      ctx.lineTo(px - r * 0.3, py + r * 0.9);
      ctx.stroke();

      // Arrow Shaft
      ctx.lineWidth = Math.max(1.5, r * 0.12);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.8, py); // Nock on string
      ctx.lineTo(px + r * 0.8, py); // Shaft front
      ctx.stroke();

      // Arrowhead (Smaller)
      ctx.beginPath();
      ctx.moveTo(px + r * 0.95, py);
      ctx.lineTo(px + r * 0.75, py - r * 0.15);
      ctx.lineTo(px + r * 0.75, py + r * 0.15);
      ctx.closePath();
      ctx.fill();

      // Fletching (feathers at the back)
      ctx.lineWidth = Math.max(1, r * 0.08);
      ctx.beginPath();
      ctx.moveTo(px - r * 0.7, py);
      ctx.lineTo(px - r * 0.9, py - r * 0.2);
      ctx.moveTo(px - r * 0.6, py);
      ctx.lineTo(px - r * 0.8, py - r * 0.2);
      ctx.moveTo(px - r * 0.7, py);
      ctx.lineTo(px - r * 0.9, py + r * 0.2);
      ctx.moveTo(px - r * 0.6, py);
      ctx.lineTo(px - r * 0.8, py + r * 0.2);
      ctx.stroke();
    } else if (shape === 'wand') {
      // Diagonal wand body, bottom-left → top-right
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(-Math.PI / 4);
      const ww = r * 0.22, wh = r * 1.50;
      ctx.fillRect(-ww / 2, -wh / 2 + r * 0.20, ww, wh);
      ctx.restore();
      // 4-point star at the tip (top-right)
      const sx = px + r * 0.58, sy = py - r * 0.58;
      const ss = r * 0.34;
      ctx.beginPath();
      ctx.moveTo(sx, sy - ss);
      ctx.lineTo(sx + ss * 0.30, sy - ss * 0.30);
      ctx.lineTo(sx + ss, sy);
      ctx.lineTo(sx + ss * 0.30, sy + ss * 0.30);
      ctx.lineTo(sx, sy + ss);
      ctx.lineTo(sx - ss * 0.30, sy + ss * 0.30);
      ctx.lineTo(sx - ss, sy);
      ctx.lineTo(sx - ss * 0.30, sy - ss * 0.30);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Draw the terrain layer (grass / burningGrass / puddle tiles).
   *
   * Called by `_battlefield` and `_deployGrid`. Always layered between
   * grid lines and bases / units so paths and combat markers remain
   * legible. Visual style intentionally low-key — semi-transparent base
   * tint plus a small vector motif (tufts for grass, ripples for puddle,
   * embers for burningGrass) so a unit standing on the cell isn't
   * obscured. c-段 is expected to upgrade these with proper textures /
   * particle anims.
   *
   * The grid is in VIEW coords (already flipped by Game for team-1
   * guest), so we iterate without any further transformation.
   */
  private _terrainLayer(grid: TerrainGrid): void {
    // Single time read per frame so all cells animate in sync.
    const t = performance.now() / 1000;
    for (let y = 0; y < BATTLE_ROWS; y++) {
      const row = grid[y];
      if (!row) continue;
      for (let x = 0; x < BATTLE_COLS; x++) {
        const cell = row[x];
        if (!cell || cell === 'none') continue;
        this._drawTerrainCell(cell, OXB + x * CB, OYB + y * CB, x, y, t);
      }
    }
  }

  /**
   * Render one terrain tile inside the cell at `(px, py)`, size `CB`.
   *
   * `(x, y)` (grid coords) seed a deterministic per-cell variation hash
   * — tufts / flames / ripples differ from cell to cell but stay stable
   * frame-to-frame so the eye doesn't see spurious shimmer. `t` is
   * `performance.now()/1000` so every animated tile uses the SAME phase
   * across the whole frame.
   *
   * Style: rect + simple Path2D, matching `_drawUnitShape`'s thematic-
   * primitive aesthetic. No gradients (Canvas2D gradients are slow on
   * mobile WebGL backends and we draw 12+ tiles per frame).
   */
  private _drawTerrainCell(
    cell: TerrainCell,
    px: number,
    py: number,
    x: number,
    y: number,
    t: number,
  ): void {
    // Tiny LCG-ish hash of grid coords — gives every cell its own
    // signature without any per-frame jitter (using x,y alone).
    const seed = (x * 31 + y * 17 + 7) % 1000;
    const seedF = (i: number, mod: number) => ((seed + i * 41) % mod) / mod;

    ctx.save();

    if (cell === 'grass') {
      // Olive-green wash.
      ctx.fillStyle = 'rgba(78,144,68,0.34)';
      ctx.fillRect(px + 1, py + 1, CB - 2, CB - 2);
      // Slightly darker speckle along the bottom — visual "ground" cue.
      ctx.fillStyle = 'rgba(50,90,40,0.18)';
      ctx.fillRect(px + 1, py + CB * 0.78, CB - 2, CB * 0.22);

      // 4–5 tufts at deterministic positions, gently swaying.
      const tuftCount = 4 + (seed & 1);
      ctx.strokeStyle = 'rgba(34,98,46,0.85)';
      ctx.lineWidth = 1.4;
      ctx.lineCap = 'round';
      for (let i = 0; i < tuftCount; i++) {
        const cx = px + CB * (0.18 + seedF(i, 100) * 0.66);
        const cy = py + CB * (0.50 + seedF(i + 7, 100) * 0.38);
        const sway = Math.sin(t * 2.0 + seed + i * 0.7) * CB * 0.04;
        ctx.beginPath();
        // Outer two blades
        ctx.moveTo(cx - CB * 0.06, cy);
        ctx.lineTo(cx - CB * 0.02 + sway, cy - CB * 0.20);
        ctx.moveTo(cx + CB * 0.06, cy);
        ctx.lineTo(cx + CB * 0.02 + sway, cy - CB * 0.20);
        // Centre blade (taller)
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + sway * 0.7, cy - CB * 0.26);
        ctx.stroke();
      }
    } else if (cell === 'burningGrass') {
      // Dark charred base.
      ctx.fillStyle = 'rgba(58,28,22,0.55)';
      ctx.fillRect(px + 1, py + 1, CB - 2, CB - 2);
      // Soot patches.
      ctx.fillStyle = 'rgba(20,8,4,0.55)';
      for (let i = 0; i < 3; i++) {
        const sx = px + CB * (0.20 + seedF(i, 100) * 0.60);
        const sy = py + CB * (0.65 + seedF(i + 11, 100) * 0.25);
        ctx.beginPath();
        ctx.arc(sx, sy, CB * (0.05 + seedF(i + 5, 100) * 0.04), 0, Math.PI * 2);
        ctx.fill();
      }
      // Faint warm wash beneath the flames so the cell glows.
      ctx.fillStyle = 'rgba(180,60,30,0.22)';
      ctx.fillRect(px + 1, py + 1, CB - 2, CB - 2);

      // 3 flames flickering at different frequencies. Each flame is two
      // stacked teardrops (outer dark-red + inner orange-yellow) drawn
      // as quadratic curves.
      const flameCount = 3;
      for (let i = 0; i < flameCount; i++) {
        const phase = seed + i * 71;
        const fx_x = px + CB * (0.27 + (i / Math.max(1, flameCount - 1)) * 0.46);
        const flicker = 0.65 + 0.35 * Math.sin(t * 8 + phase * 0.013);
        const baseY = py + CB * 0.78;
        const height = CB * 0.42 * flicker;
        const w = CB * 0.07;

        // Outer flame
        ctx.fillStyle = 'rgba(190,55,25,0.88)';
        ctx.beginPath();
        ctx.moveTo(fx_x - w, baseY);
        ctx.quadraticCurveTo(fx_x - w * 0.4, baseY - height * 0.55, fx_x, baseY - height);
        ctx.quadraticCurveTo(fx_x + w * 0.4, baseY - height * 0.55, fx_x + w, baseY);
        ctx.closePath();
        ctx.fill();
        // Inner flame
        ctx.fillStyle = 'rgba(255,180,55,0.95)';
        ctx.beginPath();
        ctx.moveTo(fx_x - w * 0.5, baseY);
        ctx.quadraticCurveTo(fx_x - w * 0.2, baseY - height * 0.45, fx_x, baseY - height * 0.78);
        ctx.quadraticCurveTo(fx_x + w * 0.2, baseY - height * 0.45, fx_x + w * 0.5, baseY);
        ctx.closePath();
        ctx.fill();
        // Bright core
        ctx.fillStyle = 'rgba(255,240,170,0.85)';
        ctx.beginPath();
        ctx.ellipse(fx_x, baseY - height * 0.30, w * 0.18, height * 0.18, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      // Occasional rising ember spark — looped 0..1 cycle, fades in/out.
      const sparkPhase = seedF(0, 100);
      const sparkT = ((t * 0.7 + sparkPhase) % 1);
      if (sparkT < 0.7) {
        const sparkX = px + CB * (0.30 + seedF(3, 100) * 0.40)
          + Math.sin(sparkT * 9 + seed) * CB * 0.04;
        const sparkY = py + CB * (0.78 - sparkT * 0.75);
        const alpha = sparkT < 0.4 ? sparkT * 2.5 : (1 - (sparkT - 0.4) / 0.3);
        ctx.fillStyle = `rgba(255,220,120,${Math.max(0, alpha) * 0.85})`;
        ctx.fillRect(sparkX - 1, sparkY - 1, 2, 2);
      }
    } else if (cell === 'puddle') {
      // Two-tone water: dark base + subtle inner highlight ellipse.
      ctx.fillStyle = 'rgba(38,86,142,0.46)';
      ctx.fillRect(px + 1, py + 1, CB - 2, CB - 2);
      const cx = px + CB / 2;
      const cy = py + CB / 2;
      ctx.fillStyle = 'rgba(85,150,210,0.22)';
      ctx.beginPath();
      ctx.ellipse(cx, cy, CB * 0.36, CB * 0.20, 0, 0, Math.PI * 2);
      ctx.fill();

      // 2 expanding ripple ellipses, looping. Different phase per cell.
      const RIPPLE_PERIOD = 2.8;
      for (let i = 0; i < 2; i++) {
        const phaseOff = seedF(i + 2, 100) + i * 0.5;
        const rt = ((t / RIPPLE_PERIOD + phaseOff) % 1);
        const r = CB * (0.06 + rt * 0.42);
        const a = (1 - rt) * 0.55;
        ctx.strokeStyle = `rgba(190,225,255,${a})`;
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r, r * 0.50, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (cell === 'boulder') {
      // Session 12 (Leader CCC, Rockfall) — chunky grey stone block.
      // Strong fill so it reads as "impassable obstacle" at a glance
      // (the other terrain tiles are translucent washes; boulder
      // deliberately breaks that pattern). No animation — boulders are
      // inert until something shatters them.
      // Body — rounded rectangle, warm-grey base + darker outline.
      const pad = 3;
      const bx = px + pad;
      const by = py + pad;
      const bw = CB - pad * 2;
      const bh = CB - pad * 2;
      ctx.fillStyle = 'rgba(122,104,82,0.92)';
      ctx.beginPath();
      // Manual rounded-rect path (avoid roundRect for wider compat).
      const r = Math.max(2, bw * 0.18);
      ctx.moveTo(bx + r, by);
      ctx.lineTo(bx + bw - r, by);
      ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + r);
      ctx.lineTo(bx + bw, by + bh - r);
      ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - r, by + bh);
      ctx.lineTo(bx + r, by + bh);
      ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - r);
      ctx.lineTo(bx, by + r);
      ctx.quadraticCurveTo(bx, by, bx + r, by);
      ctx.closePath();
      ctx.fill();
      // Darker rim — gives the stone weight.
      ctx.strokeStyle = 'rgba(58,46,32,0.95)';
      ctx.lineWidth = 1.6;
      ctx.stroke();
      // Top-left highlight wedge — fake lighting.
      ctx.fillStyle = 'rgba(210,196,170,0.32)';
      ctx.beginPath();
      ctx.moveTo(bx + r, by);
      ctx.lineTo(bx + bw * 0.55, by);
      ctx.lineTo(bx + bw * 0.20, by + bh * 0.35);
      ctx.lineTo(bx, by + bh * 0.40);
      ctx.lineTo(bx, by + r);
      ctx.quadraticCurveTo(bx, by, bx + r, by);
      ctx.closePath();
      ctx.fill();
      // 2 small fracture lines — deterministic positions, give the
      // stone texture without animating.
      ctx.strokeStyle = 'rgba(40,30,20,0.55)';
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      const fx1 = bx + bw * (0.30 + seedF(0, 100) * 0.30);
      const fy1 = by + bh * (0.45 + seedF(1, 100) * 0.20);
      ctx.moveTo(fx1, fy1);
      ctx.lineTo(fx1 + bw * 0.18, fy1 + bh * 0.10);
      ctx.moveTo(bx + bw * (0.55 + seedF(2, 100) * 0.15), by + bh * 0.70);
      ctx.lineTo(bx + bw * (0.70 + seedF(3, 100) * 0.15), by + bh * 0.85);
      ctx.stroke();
    } else if (cell === 'swamp') {
      // Session 11 (swamp) — muddy-olive base: warm brown tint with a
      // hint of green so it reads as "grass + puddle" composite.
      // Distinct from grass (saturated green), puddle (saturated blue),
      // burningGrass (red-orange).
      ctx.fillStyle = 'rgba(72,82,42,0.55)';
      ctx.fillRect(px + 1, py + 1, CB - 2, CB - 2);
      // Dark sludge patches scattered around (deterministic positions).
      ctx.fillStyle = 'rgba(36,42,20,0.55)';
      for (let i = 0; i < 4; i++) {
        const sx = px + CB * (0.15 + seedF(i, 100) * 0.70);
        const sy = py + CB * (0.20 + seedF(i + 13, 100) * 0.65);
        ctx.beginPath();
        ctx.ellipse(sx, sy, CB * (0.06 + seedF(i + 3, 100) * 0.05), CB * 0.04, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // 2–3 short grass tufts poking through the mud (subdued olive).
      ctx.strokeStyle = 'rgba(110,140,60,0.7)';
      ctx.lineWidth = 1.1;
      ctx.lineCap = 'round';
      const tuftCount = 2 + (seed & 1);
      for (let i = 0; i < tuftCount; i++) {
        const cx = px + CB * (0.22 + seedF(i + 19, 100) * 0.60);
        const cy = py + CB * (0.62 + seedF(i + 23, 100) * 0.20);
        const sway = Math.sin(t * 1.5 + seed + i * 0.9) * CB * 0.03;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + sway, cy - CB * 0.15);
        ctx.moveTo(cx + CB * 0.04, cy);
        ctx.lineTo(cx + CB * 0.03 + sway * 0.6, cy - CB * 0.12);
        ctx.stroke();
      }
      // 2 slow rising bubbles — small, dark-rimmed, looping.
      const BUBBLE_PERIOD = 3.6;
      for (let i = 0; i < 2; i++) {
        const phaseOff = seedF(i + 29, 100);
        const bt = ((t / BUBBLE_PERIOD + phaseOff) % 1);
        if (bt > 0.85) continue; // pop / fade window
        const bx = px + CB * (0.30 + seedF(i + 31, 100) * 0.40);
        const by = py + CB * (0.78 - bt * 0.50);
        const br = CB * (0.030 + bt * 0.035);
        const alpha = bt < 0.15 ? bt / 0.15 : (bt > 0.65 ? (1 - (bt - 0.65) / 0.20) : 1);
        ctx.strokeStyle = `rgba(180,200,140,${Math.max(0, alpha) * 0.7})`;
        ctx.lineWidth = 0.9;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private _battlefield(g: Game): void {
    // Session 9: battlefield is always full brightness. Earlier sessions
    // dimmed it during MATCH3 so the bottom 三消 panel grabs focus, but
    // user wants symmetrical attention — match-3 panel still desaturates
    // during SCHED/EXEC (handled in _match3), but the battlefield stays
    // at 1.0 alpha across all phases.
    const dim = 1.0;

    // Grid
    ctx.globalAlpha = dim;
    ctx.strokeStyle = 'rgba(40,60,100,0.35)';
    ctx.lineWidth = 0.5;
    for (let r = 0; r <= BATTLE_ROWS; r++) {
      ctx.beginPath(); ctx.moveTo(OXB, OYB + r * CB); ctx.lineTo(OXB + BATTLE_COLS * CB, OYB + r * CB); ctx.stroke();
    }
    for (let c = 0; c <= BATTLE_COLS; c++) {
      ctx.beginPath(); ctx.moveTo(OXB + c * CB, OYB); ctx.lineTo(OXB + c * CB, OYB + BATTLE_ROWS * CB); ctx.stroke();
    }

    // Terrain — under everything else (targeting overlay / bases / units /
    // paths / fx). View-coord grid stored on Game.terrain; iterate as-is.
    if (g.terrain) this._terrainLayer(g.terrain);

    // Targeting overlay
    if (g.targeting && g.targetPos) {
      const sp = SPELLS[ELEM[g.selSpellType]];
      const rad = sp ? sp.radius : 1;
      for (let r = 0; r < BATTLE_ROWS; r++) {
        for (let c = 0; c < BATTLE_COLS; c++) {
          if (md({ x: c, y: r }, g.targetPos) <= rad) {
            ctx.fillStyle = 'rgba(255,120,0,0.25)';
            ctx.fillRect(OXB + c * CB, OYB + r * CB, CB, CB);
          }
        }
      }
    }

    // Bases
    for (const base of g.bases) {
      const col = base.team === g.myTeam ? 'rgba(68,136,255,' : 'rgba(255,68,85,';
      for (const t of base.tiles) {
        const bx = OXB + t.x * CB;
        const by = OYB + t.y * CB;
        if (base.destroyed) {
          // Destroyed base — dark rubble
          ctx.fillStyle = 'rgba(60,30,30,0.6)';
          ctx.fillRect(bx + 2, by + 2, CB - 4, CB - 4);
          ctx.strokeStyle = 'rgba(255,60,60,0.3)'; ctx.lineWidth = 1;
          ctx.strokeRect(bx + 2, by + 2, CB - 4, CB - 4);
          // X mark
          ctx.strokeStyle = 'rgba(255,60,60,0.5)'; ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(bx + 6, by + 6); ctx.lineTo(bx + CB - 6, by + CB - 6);
          ctx.moveTo(bx + CB - 6, by + 6); ctx.lineTo(bx + 6, by + CB - 6);
          ctx.stroke();
        } else {
          // Alive base — filled tile with border
          ctx.fillStyle = col + '0.25)';
          ctx.fillRect(bx + 1, by + 1, CB - 2, CB - 2);
          ctx.strokeStyle = col + '0.7)'; ctx.lineWidth = 2;
          ctx.strokeRect(bx + 1, by + 1, CB - 2, CB - 2);
          // Castle icon (simple turret shape)
          const cx = bx + CB / 2, cy = by + CB / 2;
          const s = CB * 0.22;
          ctx.fillStyle = col + '0.8)';
          ctx.fillRect(cx - s * 1.2, cy - s * 0.5, s * 2.4, s * 1.4);
          ctx.fillRect(cx - s * 1.4, cy - s * 1.1, s * 0.5, s * 0.7);
          ctx.fillRect(cx + s * 0.9, cy - s * 1.1, s * 0.5, s * 0.7);
          ctx.fillRect(cx - s * 0.25, cy - s * 1.3, s * 0.5, s * 0.9);
        }
      }
      // Base HP bar (drawn once, spanning all tiles)
      if (!base.destroyed) {
        const minX = Math.min(...base.tiles.map(t => t.x));
        const maxX = Math.max(...base.tiles.map(t => t.x));
        const tY = base.tiles[0].y;
        const barX = OXB + minX * CB;
        const barW = (maxX - minX + 1) * CB;
        const hpRatio = clamp(base.hp / base.maxHp, 0, 1);
        const barY = base.team !== g.myTeam ? OYB + tY * CB + CB + 2 : OYB + tY * CB - 6;
        ctx.fillStyle = '#222'; ctx.fillRect(barX, barY, barW, 4);
        ctx.fillStyle = hpRatio > 0.5 ? '#44ee44' : hpRatio > 0.25 ? '#eeaa00' : '#ee3333';
        ctx.fillRect(barX, barY, barW * hpRatio, 4);
        // HP text
        ctx.fillStyle = '#ccc'; ctx.font = `bold ${Math.max(8, CB * 0.2)}px sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'top';
        ctx.fillText(`${base.hp}/${base.maxHp}`, barX + barW / 2, barY + 5);
      }
    }

    // Path preview
    if (g.state !== GS.EXEC) {
      ctx.globalAlpha = 0.2 * dim;
      for (const u of g.units) {
        if (u.dead || u.team !== g.myTeam) continue;
        ctx.strokeStyle = '#4488ff'; ctx.lineWidth = 2;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(OXB + u.pos.x * CB + CB / 2, OYB + u.pos.y * CB + CB / 2);
        for (let i = u.pathIdx; i < u.path.length; i++) {
          ctx.lineTo(OXB + u.path[i].x * CB + CB / 2, OYB + u.path[i].y * CB + CB / 2);
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.globalAlpha = dim;
    }

    // Pre-compute friendly stacking offsets:
    // Group alive units by rounded grid cell + team
    const stackMap = new Map<string, BattleUnit[]>();
    for (const u of g.units) {
      if (u.dead) continue;
      const gx = Math.round(u.vx), gy = Math.round(u.vy);
      const key = `${u.team},${gx},${gy}`;
      if (!stackMap.has(key)) stackMap.set(key, []);
      stackMap.get(key)!.push(u);
    }

    // Units
    for (const u of g.units) {
      if (u.dead) continue;
      const cfg = UCFG[u.type];

      // Compute stacking offset
      const gx = Math.round(u.vx), gy = Math.round(u.vy);
      const key = `${u.team},${gx},${gy}`;
      const stackGroup = stackMap.get(key) || [u];
      let stackOff = 0;
      if (stackGroup.length > 1) {
        const idx = stackGroup.indexOf(u);
        // Spread units horizontally within the cell
        const count = stackGroup.length;
        stackOff = (idx - (count - 1) / 2) * CB * 0.35;
      }

      const px = OXB + u.vx * CB + CB / 2 + stackOff;
      const py = OYB + u.vy * CB + CB / 2;
      const r = stackGroup.length > 1 ? CB * 0.28 : CB * 0.36; // smaller when stacked

      // Flash effect
      if (u.flash > 0) {
        ctx.globalAlpha = u.flash * dim;
        ctx.fillStyle = u.flashColor;
        ctx.fillRect(px - r - 2, py - r - 2, r * 2 + 4, r * 2 + 4);
        u.flash -= 0.02;
        ctx.globalAlpha = dim;
      }

      // Unit icon (Session 7: thematic — sword/horseshoe/bow/wand)
      const tcol = u.team === g.myTeam ? '#4488ff' : '#ff4455';
      ctx.fillStyle = tcol;
      this._drawUnitShape(cfg.shape, px, py, r);

      // HP bar
      const hpRatio = u.totalSoldiers > 0 ? u.totalSoldiers / cfg.hp : 0;
      const bw = (stackGroup.length > 1 ? CB * 0.5 : CB * 0.7), bh = 3, bx = px - bw / 2, by = py - r - 6;
      ctx.fillStyle = '#222'; ctx.fillRect(bx, by, bw, bh);
      ctx.fillStyle = hpRatio > 0.5 ? '#44ee44' : hpRatio > 0.25 ? '#eeaa00' : '#ee3333';
      ctx.fillRect(bx, by, bw * clamp(hpRatio, 0, 1), bh);

      // Soldier count
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(8, (stackGroup.length > 1 ? CB * 0.22 : CB * 0.26))}px sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(u.hp), px, py);

      // Type initial
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = `${Math.max(7, CB * 0.18)}px sans-serif`;
      ctx.fillText(u.type[0], px, py + r + 8);

      // Modifier badges (Session 10 follow-up) — row of small colored
      // circles above the HP bar showing active buffs/debuffs/shields.
      // Opacity fades with ticksRemaining/startingTicks; pulses on the
      // final tick so the player sees "this is about to expire".
      this._drawUnitModifierBadges(u, px, by);
    }
    ctx.globalAlpha = 1;
  }

  private _hud(g: Game): void {
    const y = ZHY, h = ZMY - ZHY;
    ctx.fillStyle = '#0e1328'; ctx.fillRect(0, y, W, h);
    ctx.strokeStyle = '#1a2040'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();

    // Timer + swap counter — Session 6c follow-up: hidden while a
    // transition banner is up (the big "進入魔法部屬" / "進入魔法獲取"
    // label sits at the same vertical band, otherwise they overlap).
    const inTransition = g.transitionTimer > 0;
    if ((g.state === GS.MATCH3 || g.state === GS.SCHED) && !inTransition) {
      ctx.textAlign = 'center';
      // ∞ for any local-mode session (single / story / casual) — these
      // all skip the timer countdown in update() (`_isLocalMode()` gate).
      // Showing a literal "30s" that never ticks down was confusing.
      // Keep the live countdown for PvP (pvp-host / pvp-guest) where
      // the server actually enforces a deadline.
      const isLocal = g.gameMode === 'single' || g.gameMode === 'story' || g.gameMode === 'casual';
      if (!isLocal) {
        ctx.fillStyle = g.timer < 5 ? '#ff4444' : '#44ff88';
        ctx.font = `bold ${Math.max(14, h * 0.32)}px monospace`;
        ctx.fillText(Math.ceil(Math.max(0, g.timer)) + 's', W / 2, y + h * 0.3);
      } else {
        ctx.fillStyle = '#44ff88';
        ctx.font = `bold ${Math.max(18, h * 0.4)}px sans-serif`;
        ctx.fillText('∞', W / 2, y + h * 0.3);
      }
    }
    if (g.state === GS.MATCH3 && !inTransition) {
      const remaining = Math.max(0, MAX_SWAPS_PER_ROUND - g.swapLog.length);
      ctx.fillStyle = remaining <= 2 ? '#ff8844' : '#88aacc';
      ctx.font = `${Math.max(10, h * 0.18)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${remaining} swaps`, W / 2, y + h * 0.55);
    }
    // Session 6c item 4: EXEC shows "Tick X/3 — <substep>" instead of the
    // generic "Casting..." / "Moving..." with a meaningless progress bar.
    if (g.state === GS.EXEC && g.stepIdx < g.events.length) {
      const ev = g.events[g.stepIdx];
      const tick = ('tick' in ev && typeof ev.tick === 'number') ? ev.tick + 1 : 1;
      let substep = '';
      switch (ev.type) {
        case 'tickLabel': substep = ''; break;
        case 'spell':
          substep = (ev as { phase?: string }).phase === 'friendly' ? 'Spell↑' : 'Spell↓';
          break;
        case 'move': substep = 'Move'; break;
        case 'combat': substep = 'Combat'; break;
        case 'contestResolve': substep = 'Contest'; break;
        case 'gameEnd': substep = 'Battle Over!'; break;
      }
      ctx.fillStyle = '#ffdd44';
      ctx.font = `bold ${Math.max(12, h * 0.25)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`Tick ${tick}/3${substep ? ' — ' + substep : ''}`, W / 2, y + h * 0.3);
    }

    // Soldier count bars — Phase 3d Step 4 fix: "You" is the local
    // player's team (myTeam), "Opp" is the other.
    const bw = W * 0.38, bh = h * 0.18, by = y + h * 0.50;
    const myTeam = g.myTeam;
    const oppTeam = (myTeam === 0 ? 1 : 0) as 0 | 1;
    const hpMine = g._teamHP(myTeam), hpOpp = g._teamHP(oppTeam);
    const opponentLabel = g.gameMode === 'single' ? 'AI' : 'Opp';
    const maxSoldiers = 250;

    // Player bar (left) — always blue, always own team
    ctx.fillStyle = '#181c30'; ctx.fillRect(8, by, bw, bh);
    ctx.fillStyle = 'rgba(68,136,255,0.3)';
    ctx.fillRect(8, by, bw * clamp(hpMine.total / maxSoldiers, 0, 1), bh);
    ctx.fillStyle = '#4488ff';
    ctx.fillRect(8, by, bw * clamp(hpMine.healthy / maxSoldiers, 0, 1), bh);
    ctx.fillStyle = '#fff'; ctx.font = `bold ${Math.max(8, bh * 0.7)}px sans-serif`;
    ctx.textAlign = 'left'; ctx.fillText(`You: ${hpMine.healthy}`, 12, by + bh / 2 + 1);

    // Opponent bar (right) — always red, always other team
    ctx.fillStyle = '#181c30'; ctx.fillRect(W - 8 - bw, by, bw, bh);
    ctx.fillStyle = 'rgba(255,68,85,0.3)';
    ctx.fillRect(W - 8 - bw, by, bw * clamp(hpOpp.total / maxSoldiers, 0, 1), bh);
    ctx.fillStyle = '#ff4455';
    ctx.fillRect(W - 8 - bw, by, bw * clamp(hpOpp.healthy / maxSoldiers, 0, 1), bh);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'right'; ctx.fillText(`${opponentLabel}: ${hpOpp.healthy}`, W - 12, by + bh / 2 + 1);

    // Base HP bars (below soldier bars). Show OWN base on left, OPP on right.
    const bbh = h * 0.12, bby = by + bh + 2;
    const bMine = g.bases.find(b => b.team === myTeam);
    const bOpp = g.bases.find(b => b.team === oppTeam);
    if (bMine) {
      const r0 = bMine.destroyed ? 0 : clamp(bMine.hp / bMine.maxHp, 0, 1);
      ctx.fillStyle = '#181c30'; ctx.fillRect(8, bby, bw, bbh);
      ctx.fillStyle = bMine.destroyed ? '#551122' : (r0 > 0.5 ? '#2244aa' : '#aa6600');
      ctx.fillRect(8, bby, bw * r0, bbh);
      ctx.fillStyle = '#aaa'; ctx.font = `${Math.max(7, bbh * 0.8)}px sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText(bMine.destroyed ? 'Base: DESTROYED' : `Base: ${bMine.hp}/${bMine.maxHp}`, 12, bby + bbh / 2 + 1);
    }
    if (bOpp) {
      const r1 = bOpp.destroyed ? 0 : clamp(bOpp.hp / bOpp.maxHp, 0, 1);
      ctx.fillStyle = '#181c30'; ctx.fillRect(W - 8 - bw, bby, bw, bbh);
      ctx.fillStyle = bOpp.destroyed ? '#551122' : (r1 > 0.5 ? '#aa2233' : '#aa6600');
      ctx.fillRect(W - 8 - bw, bby, bw * r1, bbh);
      ctx.fillStyle = '#aaa'; ctx.font = `${Math.max(7, bbh * 0.8)}px sans-serif`;
      ctx.textAlign = 'right';
      ctx.fillText(bOpp.destroyed ? 'Base: DESTROYED' : `Base: ${bOpp.hp}/${bOpp.maxHp}`, W - 12, bby + bbh / 2 + 1);
    }

    // Resources — Points & Tiers display.
    //   Label: "<initial>:Lv<N>"   (gray when Lv 0)
    //   Below: progress bar filling from current points → next tier
    //   threshold; bar uses pResDisplay (smoothed) so fills animate.
    //   Pop animation on collection (resFlash) — bigger pop + glow when
    //   the bar is actively catching up to a new value.
    ctx.textAlign = 'center';
    const labelY = y + h * 0.78;
    const barY = y + h * 0.90;
    const barW = 30;
    const barH = Math.max(4, h * 0.10);
    let rx = W / 2 - 90;
    // Session 9 — iterate the LEADER's spellElements (5 entries, ELEM
    // ids may be non-contiguous, e.g. BBB = [1, 2, 5, 6, 7]). Each
    // bar's HUD slot index = position in this array, the underlying
    // element id = the value at that position. All resource reads
    // (pRes / pResDisplay / resFlash) key on the element id, not the
    // slot index.
    const ldrEls = getLeader(g.playerLeader).spellElements;
    for (let i = 0; i < ldrEls.length; i++) {
      const t = ldrEls[i];
      const truePoints = g.pRes[t] || 0;
      const dispPoints = g.pResDisplay[t] ?? truePoints;
      // Level derived from the DISPLAYED value so the label flips when
      // the visual bar actually crosses the threshold.
      const lv = pointsToLevel(dispPoints);
      const flash = g.resFlash[t] || 0;
      const filling = Math.abs(truePoints - dispPoints) > 0.5; // bar is animating

      const baseColor = lv > 0 ? ECOL[ELEM[t]] : '#444';
      const isMax = lv >= SPELL_LEVEL_MAX;

      // Label scales + glows on flash. Tier-ups (flash ≥ 1.0) get an
      // extra-bright pulse so the player notices the new level number.
      const scale = 1 + flash * 0.7;
      const fontSize = Math.max(7, h * 0.14) * scale;
      ctx.font = `bold ${fontSize}px sans-serif`;
      if (flash > 0.6) {
        // Glow halo behind the text on big flashes.
        ctx.save();
        ctx.shadowColor = baseColor;
        ctx.shadowBlur = 8 + flash * 6;
        ctx.fillStyle = '#fff';
        ctx.fillText(`${_elemShort(ELEM[t])}:Lv${lv}`, rx, labelY);
        ctx.restore();
      } else {
        ctx.fillStyle = flash > 0 ? '#fff' : baseColor;
        ctx.fillText(`${_elemShort(ELEM[t])}:Lv${lv}`, rx, labelY);
      }

      // Progress bar — uses the smoothed displayed points.
      const prevThreshold = lv === 0 ? 0 : SPELL_LEVEL_THRESHOLDS[lv - 1];
      const nextThreshold = isMax
        ? prevThreshold
        : SPELL_LEVEL_THRESHOLDS[lv];
      const denom = isMax ? 1 : (nextThreshold - prevThreshold);
      const num = isMax ? 1 : (dispPoints - prevThreshold);
      const ratio = clamp(denom > 0 ? num / denom : 0, 0, 1);

      // Bar background
      ctx.fillStyle = '#181c30';
      ctx.fillRect(rx - barW / 2, barY, barW, barH);
      // Active-fill glow: while the bar is moving, paint a brighter edge
      // at the leading edge of the fill.
      if (lv > 0 || ratio > 0) {
        ctx.fillStyle = isMax ? '#ffd24a' : baseColor;
        ctx.fillRect(rx - barW / 2, barY, barW * ratio, barH);
        if (filling && ratio > 0 && ratio < 1) {
          // Bright leading edge — 3px white tip at the current fill front.
          const edgeX = rx - barW / 2 + barW * ratio;
          ctx.fillStyle = '#ffffff';
          ctx.globalAlpha = 0.85;
          ctx.fillRect(edgeX - 2, barY, 2, barH);
          ctx.globalAlpha = 1;
        }
      }
      // Bar border — pulses for MAX tier, brightens while filling.
      if (isMax) {
        ctx.strokeStyle = '#ffaa00';
        ctx.lineWidth = 1.5;
      } else if (filling) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
      } else {
        ctx.strokeStyle = 'rgba(255,255,255,0.22)';
        ctx.lineWidth = 1;
      }
      ctx.strokeRect(rx - barW / 2 + 0.5, barY + 0.5, barW - 1, barH - 1);
      rx += 40;
    }
  }

  private _match3(g: Game): void {
    // Session 6c follow-up: visual cue for read-only states.
    //  - MATCH3 phase  -> fully interactive, full colour
    //  - SCHED phase   -> dim (planning the next round)
    //  - EXEC phase    -> deeper dim with a gray wash drawn AFTER the gems
    //                    (cheap composite — ctx.filter='grayscale(...)' is
    //                    pathological on mobile/Capacitor, draws were
    //                    stuttering during EXEC tick playback)
    let dim = 1.0;
    let washColor: string | null = null;
    if (g.state === GS.SCHED) dim = 0.55;
    else if (g.state === GS.EXEC) { dim = 0.32; washColor = 'rgba(40,48,64,0.55)'; }
    ctx.globalAlpha = dim;
    // Border
    ctx.strokeStyle = 'rgba(80,120,200,0.4)'; ctx.lineWidth = 1.5;
    ctx.strokeRect(OXM - 3, OYM - 3, M3_COLS * CM + 6, M3_ROWS * CM + 6);

    // Gems
    for (const gem of g.board.getAllGems()) {
      if (gem.alpha <= 0) continue;
      const px = OXM + gem.vx * CM + CM / 2;
      const py = OYM + gem.vy * CM + CM / 2;
      const s = CM * 0.42 * gem.scale;
      if (py < OYM - CM) continue;

      ctx.globalAlpha = clamp(gem.alpha, 0, 1);
      const col = ECOL[ELEM[gem.el]];

      // Selected highlight — pulsing glow
      const isSelected = g.selectedCell && g.selectedCell.r === gem.r && g.selectedCell.c === gem.c;
      const isPressed = g.pressedCell && g.pressedCell.r === gem.r && g.pressedCell.c === gem.c;
      if (isSelected) {
        const pulse = 0.15 + 0.1 * Math.sin(g.selPulse);
        ctx.fillStyle = `rgba(255,255,255,${pulse})`;
        ctx.fillRect(OXM + gem.c * CM - 2, OYM + gem.r * CM - 2, CM + 4, CM + 4);
        ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.lineWidth = 2;
        ctx.strokeRect(OXM + gem.c * CM, OYM + gem.r * CM, CM, CM);
      }

      // Pressed feedback — slight scale up
      let drawScale = 1.0;
      if (isPressed && !isSelected) drawScale = 1.08;
      if (isSelected) drawScale = 1.05 + 0.03 * Math.sin(g.selPulse);

      // Gem body
      ctx.fillStyle = col;
      const sAdj = s * drawScale;
      const cr = sAdj * 0.3;
      ctx.beginPath();
      ctx.moveTo(px - sAdj + cr, py - sAdj);
      ctx.lineTo(px + sAdj - cr, py - sAdj);
      ctx.quadraticCurveTo(px + sAdj, py - sAdj, px + sAdj, py - sAdj + cr);
      ctx.lineTo(px + sAdj, py + sAdj - cr);
      ctx.quadraticCurveTo(px + sAdj, py + sAdj, px + sAdj - cr, py + sAdj);
      ctx.lineTo(px - sAdj + cr, py + sAdj);
      ctx.quadraticCurveTo(px - sAdj, py + sAdj, px - sAdj, py + sAdj - cr);
      ctx.lineTo(px - sAdj, py - sAdj + cr);
      ctx.quadraticCurveTo(px - sAdj, py - sAdj, px - sAdj + cr, py - sAdj);
      ctx.closePath();
      ctx.fill();

      // Shine
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fillRect(px - sAdj + sAdj * 0.2, py - sAdj + sAdj * 0.15, sAdj * 0.5, sAdj * 0.3);

      // Per-element rune (Session 9 follow-up) — replaces the legacy
      // capital-letter overlay. Adds visual character to each gem type
      // and (importantly) gives the two new whirlwind elements distinct
      // glyphs so they don't both read as "W". See `_drawGemRune`.
      this._drawGemRune(gem.el, px, py, sAdj);
      ctx.globalAlpha = 1;
    }

    // Instruction (only meaningful in MATCH3 phase)
    if (g.state === GS.MATCH3) {
      ctx.fillStyle = 'rgba(100,140,220,0.5)'; ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Swipe or tap two gems to swap', W / 2, OYM + M3_ROWS * CM + 16);
    }
    ctx.globalAlpha = 1;
    if (washColor) {
      // Cheap gray wash to fake desaturation — single fillRect, no filter.
      ctx.fillStyle = washColor;
      ctx.fillRect(OXM - 3, OYM - 3, M3_COLS * CM + 6, M3_ROWS * CM + 6);
    }
  }

  /**
   * Session 9 follow-up — short bold label drawn on every gem.
   *
   * Replaces the original `ELEM[gem.el][0]` which produced "W" for Water,
   * Wind, WindFriendly AND WindHostile (and "L" for both Light and
   * Lightning). Each ELEM now has a UNIQUE 1-char label — letter for the
   * legacy 5 (mnemonic = the *spell* the player gets, not the element
   * name itself), arrow for the two whirlwind variants (visually
   * suggestive of inward / outward swirl).
   *
   * Centralised in `_elemGemLabel` so any other place that wants the
   * same crisp gem mnemonic can reuse it. (HUD bars keep their own
   * `_elemShort` form which formats with `:Lv` suffix — see top of file.)
   */
  private _drawGemRune(el: number, cx: number, cy: number, s: number): void {
    const label = _elemGemLabel(ELEM[el]);
    // Session 9 polish — serif face (Georgia w/ cross-platform fallbacks)
    // gives single-letter gem labels a more "runic" / engraved feel than
    // the generic sans-serif used elsewhere in the UI; Cambria covers
    // Windows, Georgia covers most others, "Times New Roman" is the
    // universal fallback. The Unicode arrows (↘ ↗) fall back to the
    // OS's symbol font automatically — fine on every modern platform.
    const fontPx = Math.max(11, s * 0.78);
    ctx.font = `bold ${fontPx}px Georgia, Cambria, "Times New Roman", serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Thin dark rim makes pale letters (e.g. "B" on the bright yellow
    // Light gem) read crisply without losing the gem color underneath.
    ctx.lineWidth = Math.max(1.5, s * 0.09);
    ctx.strokeStyle = 'rgba(0,0,0,0.50)';
    ctx.lineJoin = 'round';
    ctx.strokeText(label, cx, cy);
    ctx.fillStyle = 'rgba(255,255,255,0.96)';
    ctx.fillText(label, cx, cy);
  }

  /**
   * Session 10 follow-up — render the active buff/debuff/shield badges
   * for a unit. Sits above the HP bar; each modifier kind gets a small
   * colored circle with a 1-char glyph inside.
   *
   * Visual encoding:
   *   - buffAtk positive (Bless)         → gold "↑"
   *   - buffAtk negative (Whirlwind foe) → magenta "↓"
   *   - shieldPhys (Shield Wall phys)    → green  "P"
   *   - shieldMag  (Shield Wall mag)     → cyan   "M"
   *
   * Duration cue:
   *   - opacity = max(0.5, ticksRemaining / startingTicks) — slowly
   *     fades as the buff approaches expiry
   *   - on the LAST tick (ticksRemaining === 1) the badge also pulses
   *     in scale so the player notices "ends this round"
   *
   * @param u  unit whose modifiers to draw
   * @param px unit X center
   * @param hpBarY the Y of the HP bar (badges go just above it)
   */
  private _drawUnitModifierBadges(u: BattleUnit, px: number, hpBarY: number): void {
    if (!u.modifiers || u.modifiers.length === 0) return;
    const r = Math.max(4, CB * 0.10);
    const gap = r * 2 + 2;
    // Row is centered on px; total width = (n-1) * gap, leftmost center at px - (n-1)*gap/2.
    const n = u.modifiers.length;
    const baseX = px - ((n - 1) * gap) / 2;
    const cy = hpBarY - r - 3;
    const tNow = performance.now() / 1000;
    for (let i = 0; i < n; i++) {
      const m = u.modifiers[i];
      const cx = baseX + i * gap;

      // Color + glyph by kind (debuff = negative buffAtk).
      let color: string;
      let glyph: string;
      switch (m.kind) {
        case 'buffAtk':
          if (m.value >= 0) { color = '#ffcc22'; glyph = '↑'; }
          else              { color = '#ee44bb'; glyph = '↓'; }
          break;
        case 'shieldPhys': color = '#88ffbb'; glyph = 'P'; break;
        case 'shieldMag':  color = '#4499ff'; glyph = 'M'; break;
        // Session 11 (Leader CCC) — rooted: brown/olive badge with 'R'
        // glyph. Color picked to NOT collide with the existing badge
        // palette (yellow buff / magenta debuff / mint phys / blue mag).
        case 'rooted':     color = '#cc8844'; glyph = 'R'; break;
        // Session 11 follow-up — defDown (vulnerability): red-orange
        // badge with 'D' glyph. Same hue family as the floattext so
        // they read together. Distinct from rooted's brown.
        case 'defDown':    color = '#ff6633'; glyph = 'D'; break;
        default: continue;
      }

      // Fade opacity proportional to remaining/starting (floor 0.5).
      const ratio = m.startingTicks > 0
        ? Math.max(0, Math.min(1, m.ticksRemaining / m.startingTicks))
        : 1;
      const alpha = Math.max(0.5, ratio);
      // Pulse on last tick: scale oscillates 1.0 ↔ 1.18.
      const pulse = m.ticksRemaining === 1 ? (1 + 0.18 * Math.sin(tNow * 6)) : 1;
      const drawR = r * pulse;

      // Circle body
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(cx, cy, drawR, 0, Math.PI * 2);
      ctx.fill();
      // Thin dark rim for readability against pale unit body
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Glyph (black for high-contrast on bright fills)
      ctx.fillStyle = 'rgba(0,0,0,0.85)';
      ctx.font = `bold ${Math.max(7, drawR * 1.4)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(glyph, cx, cy + 0.5);
      ctx.restore();
    }
  }

  /**
   * Session 6c follow-up: tab strip at the top of the interaction zone.
   * Two tabs: '三消' (match3 board) and '排程' (spell scheduling). Active
   * tab is highlighted; tap a tab to toggle `Game.bottomTab`.
   *
   * Layout: 5px from the top edge of the interaction zone, 24px tall,
   * each tab takes half the width.
   */
  private _battleTabs(g: Game): void {
    const tabsY = ZMY;
    const tabsH = 22;
    const halfW = W / 2;
    const tabs: Array<{ key: 'match3' | 'sched'; label: string }> = [
      { key: 'match3', label: '三消' },
      { key: 'sched', label: '魔法部屬' },
    ];
    for (let i = 0; i < tabs.length; i++) {
      const t = tabs[i];
      const tx = i * halfW;
      const active = g.bottomTab === t.key;
      ctx.fillStyle = active ? '#1a2545' : '#0a0e1c';
      ctx.fillRect(tx, tabsY, halfW, tabsH);
      // Bottom border (active = blue, inactive = dim).
      ctx.fillStyle = active ? '#4488ff' : '#2a2e44';
      ctx.fillRect(tx, tabsY + tabsH - 2, halfW, 2);
      // Top + side borders for separation.
      ctx.strokeStyle = '#1a2040'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(tx, tabsY); ctx.lineTo(tx + halfW, tabsY); ctx.stroke();
      if (i === 0) {
        ctx.beginPath(); ctx.moveTo(halfW, tabsY); ctx.lineTo(halfW, tabsY + tabsH); ctx.stroke();
      }
      // Label.
      ctx.fillStyle = active ? '#cce' : '#778';
      ctx.font = `bold ${Math.max(11, tabsH * 0.55)}px sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(t.label, tx + halfW / 2, tabsY + tabsH / 2);
    }
  }

  /**
   * Session 6c follow-up: sticky phase banner above the battlefield. Set
   * by Game._applyEvent at each EXEC sub-step (Friendly Spells / Move /
   * Combat / Hostile Spells / Contest) and stays put until replaced by
   * the next sub-step. No fade-out — clears between rounds.
   */
  private _phaseBanner(g: Game): void {
    if (!g.phaseBanner) return;
    ctx.fillStyle = g.phaseBanner.color;
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.fillText(g.phaseBanner.text, W / 2, OYB - 8);
  }

  /**
   * Session 6c follow-up: scheduling overlay redesigned for compactness
   * (no overflow on phone screens). Layout — top of interaction zone:
   *
   *   [tab strip 22px]
   *   [Slot 1] [Slot 2] [Slot 3]      <— always; current slot highlighted
   *   <body>                          <— spell list / target prompt /
   *                                      [Back] [Confirm Plan] (split row)
   *
   * Removed in this redesign:
   *  - "Spell Scheduling" title (redundant — tab already says 魔法部屬)
   *  - "All 3 ticks set. Press Confirm to lock in." help text
   *  - Stacked Back row above the Confirm CTA — now Back + Confirm sit
   *    side-by-side in the confirm step.
   */
  private _scheduling(g: Game): void {
    const tabH = 22;
    const zy = ZMY + tabH + 4;

    // Translucent backdrop over interaction zone — board still peeks through.
    ctx.fillStyle = 'rgba(8,12,28,0.72)';
    ctx.fillRect(0, ZMY + tabH, W, H - ZMY - tabH);

    // Slots row.
    const sw = W * 0.30, sh = 50, sy = zy + 2, gap = (W - sw * 3) / 4;
    for (let i = 0; i < 3; i++) {
      const sx = gap + i * (sw + gap);
      const active = i === g.spellSlot && g.schedStep !== 'confirm' && g.state === GS.SCHED;
      ctx.fillStyle = active ? '#1a2545' : '#111830';
      ctx.strokeStyle = active ? '#4488ff' : '#2a2e44'; ctx.lineWidth = active ? 2 : 1;
      ctx.fillRect(sx, sy, sw, sh); ctx.strokeRect(sx, sy, sw, sh);
      ctx.fillStyle = '#8899aa'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`Tick ${i + 1}`, sx + sw / 2, sy + 14);
      const sp = g.pSpells[i];
      if (sp && sp.spellType > 0) {
        ctx.fillStyle = ECOL[ELEM[sp.spellType]];
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText(ELEM[sp.spellType], sx + sw / 2, sy + 32);
        ctx.fillStyle = '#aaa'; ctx.font = '10px sans-serif';
        ctx.fillText(`Lv.${sp.powerLevel}`, sx + sw / 2, sy + 46);
      } else if (sp) {
        ctx.fillStyle = '#555'; ctx.font = '12px sans-serif';
        ctx.fillText('(Pass)', sx + sw / 2, sy + 32);
      }
    }
    const slotsBottom = sy + sh;

    // ----- Outside SCHED phase: read-only slot summary, nothing else. -----
    if (g.state !== GS.SCHED) return;

    // ----- Inside SCHED phase: body content per schedStep. -----
    const bodyTop = slotsBottom + 12;

    // 'target' — prompt only (no Back button shown here; user taps the
    // battlefield. They can still cancel via the back button in 'slot' /
    // 'confirm' steps; while targeting we steer the click).
    if (g.schedStep === 'target') {
      ctx.fillStyle = '#ffaa44'; ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      ctx.fillText('Tap battlefield to set target', W / 2, bodyTop + 24);
      // Small Back affordance below the prompt.
      const bw = W * 0.28, bh = 30, bx = (W - bw) / 2, by = bodyTop + 40;
      ctx.fillStyle = '#2a1f1a'; ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = '#cc7744'; ctx.lineWidth = 1.5;
      ctx.strokeRect(bx, by, bw, bh);
      ctx.fillStyle = '#ffaa66'; ctx.font = 'bold 12px sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText('← Back', bx + bw / 2, by + bh / 2);
      ctx.textBaseline = 'alphabetic';
      return;
    }

    // 'confirm' — Back + Confirm side-by-side in a single row.
    if (g.schedStep === 'confirm') {
      const rowY = bodyTop + 8;
      const rowH = 48;
      const padX = W * 0.05;
      const totalW = W - padX * 2;
      const gapX = 10;
      const backW = totalW * 0.30;
      const confirmW = totalW - backW - gapX;

      // Back.
      ctx.fillStyle = '#2a1f1a'; ctx.fillRect(padX, rowY, backW, rowH);
      ctx.strokeStyle = '#cc7744'; ctx.lineWidth = 1.5;
      ctx.strokeRect(padX, rowY, backW, rowH);
      ctx.fillStyle = '#ffaa66'; ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('← Back', padX + backW / 2, rowY + rowH / 2);

      // Confirm.
      const cx = padX + backW + gapX;
      ctx.fillStyle = '#1a3025'; ctx.fillRect(cx, rowY, confirmW, rowH);
      ctx.strokeStyle = '#44dd88'; ctx.lineWidth = 2;
      ctx.strokeRect(cx, rowY, confirmW, rowH);
      ctx.fillStyle = '#88ffaa'; ctx.font = 'bold 17px sans-serif';
      ctx.fillText('✓ Confirm Plan', cx + confirmW / 2, rowY + rowH / 2);
      ctx.textBaseline = 'alphabetic';
      return;
    }

    // 'slot' — Back (only if undoable) + spell list + Pass button.
    let listY = bodyTop;

    // Back button — small, top-right of the body when there's something
    // to undo (spellSlot > 0).
    if (g.spellSlot > 0) {
      const bw = W * 0.24, bh = 28, bx = W - W * 0.05 - bw, by = bodyTop;
      ctx.fillStyle = '#2a1f1a'; ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = '#cc7744'; ctx.lineWidth = 1.5;
      ctx.strokeRect(bx, by, bw, bh);
      ctx.fillStyle = '#ffaa66'; ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('← Back', bx + bw / 2, by + bh / 2);
      ctx.textBaseline = 'alphabetic';
      listY = bodyTop + bh + 10;
    }

    // Spell list — only during SCHED phase (we already gated that earlier
    // by the read-only return; reaching here means state === GS.SCHED).
    if (g.spellSlot < 3) {
      let by = listY;
      for (const sp of g.availSpells) {
        const name = SPELLS[ELEM[sp.el]]?.name || '?';
        const col = ECOL[ELEM[sp.el]];
        ctx.fillStyle = '#141d35'; ctx.fillRect(W * 0.08, by, W * 0.84, 36);
        ctx.strokeStyle = col; ctx.lineWidth = 1.5;
        ctx.strokeRect(W * 0.08, by, W * 0.84, 36);
        ctx.fillStyle = col; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(`${name} Lv.${sp.lv}`, W * 0.13, by + 22);
        ctx.fillStyle = '#aaa'; ctx.font = '11px sans-serif'; ctx.textAlign = 'right';
        const sd = SPELLS[ELEM[sp.el]];
        const desc = _spellDesc(sd);
        ctx.fillText(desc, W * 0.87, by + 22);
        by += 42;
      }
      // Pass.
      ctx.fillStyle = '#1a1a2a'; ctx.fillRect(W * 0.08, by, W * 0.84, 34);
      ctx.strokeStyle = '#555'; ctx.lineWidth = 1;
      ctx.strokeRect(W * 0.08, by, W * 0.84, 34);
      ctx.fillStyle = '#999'; ctx.font = '13px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('Pass (skip this tick)', W / 2, by + 22);
    }
  }

  private _gameOver(g: Game): void {
    const cy = ZMY + (H - ZMY) / 2;
    // Phase 3d Step 4 fix: VICTORY/DEFEAT relative to local player's team.
    const myTeam = g.myTeam;
    const oppTeam = (myTeam === 0 ? 1 : 0) as 0 | 1;
    const won = g.winner === myTeam;
    const lost = g.winner === oppTeam;
    ctx.fillStyle = won ? '#2255aa' : lost ? '#aa2233' : '#555';
    ctx.font = 'bold 28px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(won ? 'VICTORY!' : lost ? 'DEFEAT' : 'DRAW', W / 2, cy - 40);
    // Phase 3d Step 5: explain the result if opponent dropped out mid-battle.
    // selfDisconnected mirrors this for the side whose own socket dropped —
    // shows DEFEAT + "(連線中斷)" instead of leaving them stuck behind the
    // "Waiting for opponent" overlay.
    if (g.opponentLeft) {
      ctx.fillStyle = '#ffaa44';
      ctx.font = '14px sans-serif';
      ctx.fillText('(Opponent disconnected)', W / 2, cy - 18);
    } else if (g.selfDisconnected) {
      ctx.fillStyle = '#ffaa44';
      ctx.font = '14px sans-serif';
      ctx.fillText('(連線中斷)', W / 2, cy - 18);
    }
    ctx.fillStyle = '#aaa'; ctx.font = '14px sans-serif';
    const fhpMine = g._teamHP(myTeam), fhpOpp = g._teamHP(oppTeam);
    const bMine = g.bases.find(b => b.team === myTeam);
    const bOpp = g.bases.find(b => b.team === oppTeam);
    const opponentLabel = g.gameMode === 'single' ? 'AI' : 'Opp';
    ctx.fillText(`You: ${fhpMine.healthy} soldiers | ${opponentLabel}: ${fhpOpp.healthy} soldiers`, W / 2, cy - 10);
    // Base status
    ctx.font = '12px sans-serif'; ctx.fillStyle = '#888';
    const bMineStatus = bMine ? (bMine.destroyed ? 'DESTROYED' : `${bMine.hp}/${bMine.maxHp} HP`) : '—';
    const bOppStatus = bOpp ? (bOpp.destroyed ? 'DESTROYED' : `${bOpp.hp}/${bOpp.maxHp} HP`) : '—';
    ctx.fillText(`Your Base: ${bMineStatus} | ${opponentLabel} Base: ${bOppStatus}`, W / 2, cy + 8);
    // Restart / Retry button
    // Story Mode: while the post/lose dialogue is on screen, suppress
    // the button so the player reads the dialogue first. After it's
    // dismissed, swap the label to "Retry" on lose so the player knows
    // tapping replays the chapter rather than returning to MENU.
    const story = g.storyController;
    const hideBtn = story?.dialogueActive ?? false;
    if (!hideBtn) {
      const isStoryLose = !!(story?.battleOver) && lost;
      const isStoryWin = !!(story?.battleOver) && won;
      // Casual mode shares Story Mode's "Retry / Back to Picker" semantics
      // (loss → retry same level, win → return to level picker) so the
      // player doesn't bounce back to MENU after every casual battle.
      const isCasual = g.gameMode === 'casual';
      const isCasualLose = isCasual && lost;
      const isCasualWin = isCasual && (won || g.winner === -1);
      let label: string;
      let accent: string;
      let text: string;
      if (isStoryLose) {
        label = 'Retry Chapter'; accent = '#ffaa66'; text = '#ffcc99';
      } else if (isStoryWin) {
        label = 'Back to Chapters'; accent = '#66dd88'; text = '#aaffbb';
      } else if (isCasualLose) {
        label = 'Retry Level'; accent = '#66ddcc'; text = '#aaeedd';
      } else if (isCasualWin) {
        label = 'Back to Levels'; accent = '#66ddcc'; text = '#aaeedd';
      } else {
        label = 'Play Again'; accent = '#4488ff'; text = '#88bbff';
      }
      ctx.fillStyle = '#1a2545'; ctx.fillRect(W * 0.25, cy + 30, W * 0.5, 44);
      ctx.strokeStyle = accent; ctx.lineWidth = 2; ctx.strokeRect(W * 0.25, cy + 30, W * 0.5, 44);
      ctx.fillStyle = text; ctx.font = 'bold 16px sans-serif';
      ctx.fillText(label, W / 2, cy + 55);
    }
  }

  private _fx(g: Game): void {
    for (const f of g.fx) {
      ctx.globalAlpha = clamp(f.alpha, 0, 1);

      if (f.type === 'floatText') {
        ctx.fillStyle = f.color; ctx.font = `bold ${f.size || 14}px sans-serif`;
        ctx.textAlign = 'center'; ctx.fillText(f.text!, f.x!, f.y!);
      }

      if (f.type === 'm3Score') {
        // "+N" point gain text travelling from gem centre toward HUD bar.
        // Game.update tweens (x,y) toward (x2,y2); we draw with a soft
        // outline to keep readability over the busy gem grid.
        const fs = f.size || 14;
        ctx.font = `bold ${fs}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.strokeText(f.text!, f.x!, f.y!);
        ctx.fillStyle = f.color;
        ctx.fillText(f.text!, f.x!, f.y!);
      }

      if (f.type === 'comboBanner') {
        // Combo banner: large bold text scaled by f.scale, with soft halo.
        const baseSize = f.size || 18;
        const sc = f.scale || 1;
        ctx.font = `bold ${Math.round(baseSize * sc)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.save();
        ctx.shadowColor = f.color;
        ctx.shadowBlur = 14;
        // Double-stroke for chunky readability over the gem board.
        ctx.lineWidth = 5;
        ctx.strokeStyle = 'rgba(0,0,0,0.78)';
        ctx.strokeText(f.text!, f.x!, f.y!);
        ctx.fillStyle = f.color;
        ctx.fillText(f.text!, f.x!, f.y!);
        ctx.restore();
      }

      if (f.type === 'flash') {
        // Full-screen colour overlay — drawn last (FX render order) so it
        // tints everything underneath. globalAlpha is already set above
        // to clamp(f.alpha, 0, 1), so we just fill the whole canvas.
        ctx.fillStyle = f.color;
        ctx.fillRect(0, 0, W, H);
      }

      if (f.type === 'ring') {
        ctx.strokeStyle = f.color; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.arc(f.x!, f.y!, Math.max(0, f.radius!), 0, Math.PI * 2); ctx.stroke();
      }

      if (f.type === 'slash') {
        ctx.strokeStyle = f.color; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(f.x1!, f.y1!); ctx.lineTo(f.x2!, f.y2!); ctx.stroke();
        // Arrow head
        const angle = Math.atan2(f.y2! - f.y1!, f.x2! - f.x1!);
        ctx.beginPath();
        ctx.moveTo(f.x2!, f.y2!);
        ctx.lineTo(f.x2! - 10 * Math.cos(angle - 0.35), f.y2! - 10 * Math.sin(angle - 0.35));
        ctx.moveTo(f.x2!, f.y2!);
        ctx.lineTo(f.x2! - 10 * Math.cos(angle + 0.35), f.y2! - 10 * Math.sin(angle + 0.35));
        ctx.stroke();
      }

      if (f.type === 'moveTrail') {
        ctx.strokeStyle = f.color; ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(f.x1!, f.y1!); ctx.lineTo(f.x2!, f.y2!); ctx.stroke();
        ctx.setLineDash([]);
      }

      if (f.type === 'moveArrow') {
        // Solid arrow showing movement direction
        const angle = Math.atan2(f.y2! - f.y1!, f.x2! - f.x1!);
        const sz = 6;
        ctx.fillStyle = f.color;
        ctx.beginPath();
        ctx.moveTo(f.x2! + sz * Math.cos(angle), f.y2! + sz * Math.sin(angle));
        ctx.lineTo(f.x2! - sz * Math.cos(angle - 0.6), f.y2! - sz * Math.sin(angle - 0.6));
        ctx.lineTo(f.x2! - sz * Math.cos(angle + 0.6), f.y2! - sz * Math.sin(angle + 0.6));
        ctx.closePath(); ctx.fill();
      }

      if (f.type === 'particle') {
        ctx.fillStyle = f.color;
        const ps = f.size || 3;
        ctx.beginPath(); ctx.arc(f.x!, f.y!, ps, 0, Math.PI * 2); ctx.fill();
      }

      if (f.type === 'deathX') {
        // Draw an X mark where unit died
        const s = (f.size || 10) * (f.scale || 1);
        ctx.strokeStyle = f.color; ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(f.x! - s, f.y! - s); ctx.lineTo(f.x! + s, f.y! + s);
        ctx.moveTo(f.x! + s, f.y! - s); ctx.lineTo(f.x! - s, f.y! + s);
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
    }
  }

  private _transition(g: Game): void {
    if (g.transitionTimer <= 0) return;
    // Fade-in banner across the middle of the screen. Stays at full alpha
    // for the bulk of the timer, fades out in the last 0.35s. Works for
    // both the short (0.6s) round-start banner AND the longer (1.4s)
    // MATCH3→SCHED entrance banner (Session 6c follow-up).
    const t = g.transitionTimer;
    const fadeWindow = 0.35;
    const alpha = t > fadeWindow ? 1 : Math.max(0, t / fadeWindow);
    ctx.globalAlpha = alpha * 0.65;
    ctx.fillStyle = '#000';
    const bh = 56;
    ctx.fillRect(0, H / 2 - bh / 2, W, bh);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#ffdd44';
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(g.transitionLabel, W / 2, H / 2);
    ctx.globalAlpha = 1;
  }

  /**
   * Session 6c follow-up — loading spinner shown while the client is
   * still talking to the server (initBattle in flight, or PvP lobby
   * waiting for opponent's initBattle to land). Sits over whatever
   * screen is currently visible (DEPLOY / LOBBY) so the player knows
   * the app isn't frozen. Spinning ring + label + animated dots.
   */
  private _loadingOverlay(g: Game): void {
    // Dim everything behind us.
    ctx.fillStyle = 'rgba(8,12,28,0.78)';
    ctx.fillRect(0, 0, W, H);

    const cx = W / 2;
    const cy = H / 2;
    const radius = 22;

    // Spinner ring.
    const t = Date.now() / 1000;
    ctx.strokeStyle = 'rgba(80,140,220,0.25)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(cx, cy - 16, radius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = '#88bbff';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    const start = (t * 2.4) % (Math.PI * 2);
    ctx.beginPath();
    ctx.arc(cx, cy - 16, radius, start, start + Math.PI * 1.2);
    ctx.stroke();
    ctx.lineCap = 'butt';

    // Label.
    const dots = '.'.repeat((((Date.now() / 350) | 0) % 4));
    const label = (g as Game & { loadingLabel?: string }).loadingLabel ?? 'Connecting to server';
    ctx.fillStyle = '#cce0ff';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label + dots, cx, cy + 24);
  }
}
